import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../i18n';
import { makeMatcher } from '../utils/search';
import Layout from '../components/Layout';
import { warehouseAPI, salesAPI, qualityAPI, exportAPI, productionAPI, shipNoteAPI } from '../api';
import toast from 'react-hot-toast';
import CatalogTree, { useFolders, inFolder, treeOf } from '../components/CatalogTree';
import StockFreshness from '../components/StockFreshness';
import { confirmToast } from '../components/ConfirmToast';
import { useBranch } from '../contexts/BranchContext';
import ClientPicker from '../components/ClientPicker';
import SuggestInput from '../components/SuggestInput';
import ShipmentNotes from './ShipmentNotes';
import OrderSheet from './OrderSheet';
import { ApprovalTable } from './Technolog';
import { ProductionRequests } from './ProductionRequests';
import { TileGrid, TilesHead, BackHome } from '../components/Tiles';

// ─── Константы ────────────────────────────────────────────────────────────────

// Производителя плиты и упаковку называет клиент, а не мы: справочника для них
// нет и быть не может. Поэтому — история ввода: набранное однажды предлагается
// со следующего раза.
//
// Копилка общая с заявкой на производство. Ключи там исторические
// (plate_size — «Размер плиты», note — «Примечание»), а спрашивают эти поля
// давно другое; переименовать ключи значило бы потерять всё накопленное.
const KIND_DSP  = 'plate_size';
const KIND_PACK = 'note';
const CLIENT_TYPE_CFG = {
  buyer:    { label: 'Покупатель',             color: 'var(--accent)' },
  supplier: { label: 'Поставщик',              color: '#f59e0b' },
  both:     { label: 'Покупатель+Поставщик',   color: '#22c55e' },
};

const STATUS_CFG = {
  pending:    { label: 'Ожидает согласования', color: '#f97316', bg: '#f9731618' },
  // Заказ целиком закрывается складом: цеху делать нечего, ждём только
  // разрешения выпустить его со двора
  stock:      { label: 'Со склада — ждёт разрешения', color: '#0ea5e9', bg: '#0ea5e918' },
  queue:      { label: 'В очереди',            color: '#f59e0b', bg: '#f59e0b18' },
  production: { label: 'В производстве',       color: 'var(--accent)', bg: 'var(--accent-weak)' },
  ready:      { label: 'Готов к отгрузке',     color: '#22c55e', bg: '#22c55e18' },
  shipped:    { label: 'Отгружен',             color: '#8b5cf6', bg: '#8b5cf618' },
  // Товар увезли отдельной отгрузкой, а заказ висел открытым и держал резерв
  closed:     { label: 'Закрыт',               color: '#64748b', bg: '#64748b18' },
  cancelled:  { label: 'Отменён',              color: '#ef4444', bg: '#ef444418' },
};

// ─── Deadline helper ──────────────────────────────────────────────────────────
function getDeadlineInfo(deadline, status) {
  if (!deadline || ['shipped', 'cancelled'].includes(status)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dl = new Date(deadline);
  dl.setHours(0, 0, 0, 0);
  const diffDays = Math.round((dl - today) / 86400000);

  if (diffDays < 0)  return { label: `Просрочен на ${-diffDays} дн.`, color: '#ef4444', bg: '#ef444420', icon: '🚨' };
  if (diffDays === 0) return { label: 'Срок сегодня!',                color: '#ef4444', bg: '#ef444418', icon: '🔴' };
  if (diffDays === 1) return { label: 'Срок завтра',                  color: '#f97316', bg: '#f9731618', icon: '🟠' };
  if (diffDays <= 3)  return { label: `Срок через ${diffDays} дн.`,   color: '#f59e0b', bg: '#f59e0b14', icon: '🟡' };
  return null;
}

// Разделы сгруппированы по смыслу: сверху то, с чем работают каждый день,
// ниже — справочники, куда заходят изредка. Рисуются в общем боковом меню
// (Layout), под пунктом «Продажи и заказы»: второе меню внутри страницы
// занимало место и спорило с первым за внимание.
// У первой группы заголовка нет намеренно — над ней уже стоит «Работа».
// Что видит менеджер. Строится при отрисовке, а не при загрузке файла:
// два раздела зависят от личных прав, и читать их надо тогда, когда человек
// уже вошёл.
//
// Заявку на производство выписывает плановый отдел — решить, какой заказ
// пустить сегодня и в какую смену. Раньше это могла только роль «Технолог», и
// ради одной обязанности человеку отдавали весь технологический справочник.
function buildGroups({ canPlan, canShip }) {
  return [
    { title: '', items: [
      { key: 'home',        label: '🏠 Главное' },
    ]},
    { title: 'Каждый день', items: [
      { key: 'orders',      label: '📋 Заказы' },
      // Между заказом и складом раньше стоял человек с бумажкой. Теперь один
      // документ: кому, по какому заказу, сколько чего — и склад видит только
      // выписанное.
      //
      // Право личное, галочкой: отгрузкой на заводе занимается один человек, а
      // должность про это ничего не говорит. Раздел висел у всех менеджеров, и
      // тот, у кого права нет, доходил до конца и получал отказ от сервера —
      // пункт меню, ведущий в стену. Показываем только тому, кто вправе.
      ...(canShip ? [{ key: 'shipnotes', label: '🚚 Выписать отгрузку' }] : []),
      { key: 'feasibility', label: '📊 Отчёт по заказам' },
      // Тот самый лист, который менеджер держал на столе и переписывал
      // от руки: декоры строками, матрицы столбцами.
      { key: 'sheet', label: '🖨 Бланк заказов' },
    ]},
    ...(canPlan ? [{ title: 'Производство', items: [
      { key: 'approval', label: '✅ Что запустить в работу' },
      { key: 'requests', label: '📄 Заявки на производство' },
    ]}] : []),
    { title: 'Справочники', items: [
      { key: 'stock',   label: '📦 Склад сырья' },
      { key: 'clients', label: '👥 Контрагенты' },
    ]},
  ];
}

// ─── UI-компоненты ────────────────────────────────────────────────────────────
// Сколько этой позиции завод может отдать: со склада ПЛЮС из сырья.
//
// Раньше два числа считались альтернативами — «хватает готового» ИЛИ «хватает
// сырья». Заказ на 120 при 100 готовых и сырье ещё на 30 не проходил ни по
// одному условию и попадал в «не хватает»: менеджеру его брать запрещали,
// хотя 100 отдали бы со склада, 20 сделали бы из сырья, и оставался запас.
//
// Сервер так и считает (sales/services.plan_items): сперва берёт со склада,
// остаток производит по рецепту, и бронирует то и другое порознь. Экран обязан
// показывать ровно это — иначе он спорит с тем, что произойдёт.
//
// Оба числа уже за вычетом брони: чужой заказ, взявший плёнку вчера, сюда не
// попадёт. Поэтому следующий заказ видит меньше — и это правильно.
function capacityOf(p) {
  const stock = Number(p.stock) || 0;
  const prod = (p.has_recipe && p.max_producible != null)
    ? Number(p.max_producible) : 0;
  return { stock, prod, total: stock + prod };
}

const Card = ({ children, style = {} }) => (
  <div style={{ background: 'var(--card)', borderRadius: 12, padding: 20, ...style }}>
    {children}
  </div>
);

const STitle = ({ children }) => (
  <h2 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
    {children}
  </h2>
);

const Badge = ({ status }) => {
  const cfg = STATUS_CFG[status] || { label: status, color: '#94a3b8', bg: '#94a3b818' };
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600
    }}>
      {cfg.label}
    </span>
  );
};

const Inp = ({ label, ...props }) => (
  <div>
    {label && <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>{label}</label>}
    <input {...props} style={{
      width: '100%', padding: '9px 12px', background: 'var(--input)',
      border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
      fontSize: 14, outline: 'none', boxSizing: 'border-box', ...props.style
    }} />
  </div>
);

const Sel = ({ label, children, ...props }) => (
  <div>
    {label && <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>{label}</label>}
    <select {...props} style={{
      width: '100%', padding: '9px 12px', background: 'var(--input)',
      border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
      fontSize: 14, outline: 'none', boxSizing: 'border-box', ...props.style
    }}>
      {children}
    </select>
  </div>
);

// Текст ошибки от сервера. DRF заворачивает ValidationError в массив, и
// toast показывал «[object Object]» вместо внятного «не хватает плёнки».
const errText = (err, fallback) => {
  const d = err && err.response && err.response.data;
  const e = d && (d.error || d.detail);
  if (Array.isArray(e)) return e.join('; ');
  if (typeof e === 'string') return e;
  return fallback;
};

// ─── Заявка на закупку: печать критического запаса ────────────────────────────
//
// Список нужен снабженцу на бумаге — с ним идут к поставщику. На экране он
// показывает только «сколько есть», а закупщику важно «сколько ДОБРАТЬ до
// минимума»: этот столбец и есть заявка.
//
// Минимум не задан, а остаток ушёл в минус — добрать надо хотя бы до нуля:
// минус означает, что товар выдали, а прихода не было.
const CAT_TITLES = {
  paper: 'Бумага / Плёнка', dsp: 'ДСП', ldsp: 'ЛДСП', other: 'Прочее',
};

function printLowStock(items) {
  const num = (n) => Number(n || 0).toLocaleString('ru-RU');
  const rows = items.map(i => {
    const target = i.min_stock > 0 ? i.min_stock : 0;
    return { ...i, need: Math.max(0, target - i.available_quantity) };
  }).sort((a, b) => b.need - a.need);

  const groups = {};
  rows.forEach(r => { (groups[r.category || 'other'] = groups[r.category || 'other'] || []).push(r); });

  const today = new Date().toLocaleDateString('ru-RU');
  const totalNeed = rows.reduce((s, r) => s + r.need, 0);

  const body = Object.keys(groups).map(cat => {
    const list = groups[cat];
    return `
      <tr class="grp"><td colspan="6">${CAT_TITLES[cat] || cat} — ${list.length} поз.</td></tr>
      ${list.map((r, i) => `
        <tr>
          <td class="c">${i + 1}</td>
          <td>${r.name}</td>
          <td class="c">${r.unit || ''}</td>
          <td class="r">${r.min_stock > 0 ? num(r.min_stock) : '—'}</td>
          <td class="r ${r.available_quantity < 0 ? 'neg' : ''}">${num(r.available_quantity)}</td>
          <td class="r need">${r.need > 0 ? num(r.need) : '—'}</td>
        </tr>`).join('')}
    `;
  }).join('');

  // @page margin:0 + пустой <title> — иначе браузер печатает свои
  // колонтитулы с адресом и датой. Поля рисуем сами отступами листа.
  const doc = `<!DOCTYPE html><html><head><meta charset="utf-8"><title></title><style>
    @page { size: A4 portrait; margin: 0; }
    * { box-sizing: border-box; }
    body { margin:0; font-family: Arial, sans-serif; color:#000; background:#fff; }
    .sheet { padding: 20mm 15mm 15mm; }
    h1 { font-size: 16pt; margin:0 0 2mm; }
    .sub { font-size: 9pt; color:#555; margin-bottom: 6mm; }
    table { width:100%; border-collapse: collapse; table-layout: fixed; }
    th, td { border:1px solid #999; padding: 1.6mm 2mm; font-size: 9pt; vertical-align: top;
             word-wrap: break-word; }
    th { background:#eee; font-size: 8.5pt; text-transform: uppercase; letter-spacing:.3px; }
    .c { text-align:center; } .r { text-align:right; }
    .neg { color:#c00; font-weight:bold; }
    .need { font-weight:bold; }
    tr.grp td { background:#f4f4f4; font-weight:bold; font-size:9pt; }
    tfoot td { font-weight:bold; background:#eee; }
    .sign { margin-top: 12mm; display:flex; gap:20mm; font-size:9.5pt; }
    .sign div { flex:1; border-top:1px solid #000; padding-top:2mm; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
  </style></head><body><div class="sheet">
    <h1>Заявка на закупку сырья</h1>
    <div class="sub">LamTech · Чимкент · ${today} · позиций: ${rows.length}</div>
    <table>
      <thead><tr>
        <th style="width:8mm">№</th>
        <th>Наименование</th>
        <th style="width:14mm">Ед.</th>
        <th style="width:22mm">Мин. запас</th>
        <th style="width:24mm">Остаток</th>
        <th style="width:26mm">Закупить</th>
      </tr></thead>
      <tbody>${body}</tbody>
      <tfoot><tr>
        <td colspan="5" class="r">ИТОГО закупить</td>
        <td class="r">${num(totalNeed)}</td>
      </tr></tfoot>
    </table>
    <div class="sign"><div>Составил</div><div>Утвердил</div></div>
  </div></body></html>`;

  // Рамку прячем за экран, но с реальным размером листа: у нулевого iframe
  // нечего верстать — браузер печатает пустую страницу.
  const f = document.createElement('iframe');
  f.style.cssText = 'position:fixed;left:-10000px;top:0;width:794px;height:1123px;border:0;';
  document.body.appendChild(f);
  f.contentDocument.open(); f.contentDocument.write(doc); f.contentDocument.close();
  setTimeout(() => {
    f.contentWindow.focus(); f.contentWindow.print();
    setTimeout(() => document.body.removeChild(f), 1000);
  }, 250);
}

// ─── Текстура товара ──────────────────────────────────────────────────────────
//
// Менеджер выбирал товар, а текстуру к нему доставал из выпадающего списка
// руками — хотя она уже написана в самом названии: «Айскрим (глянец) ЛДСП».
// Лишний шаг и лишний повод ошибиться.
//
// Поле «матрица» у товара было бы точнее, но заполнено оно у 5 позиций из 1067,
// поэтому основной путь — разобрать название.
//
// Сравниваем «сжатыми» строками: в названиях 152 позиции написано «молет», а
// текстура заведена как «Моллет» — одна лишняя буква рушила всё совпадение.
const texKey = (s) => String(s || '')
  .toLowerCase()
  .replace(/ё/g, 'е')
  .replace(/[^a-zа-я]+/g, '')      // цифры, скобки и размеры только мешают
  .replace(/(.)\1+/g, '$1');        // «моллет» и «молет» -> «молет»

function guessTexture(product, textures) {
  if (!product) return '';
  if (product.matrix) return String(product.matrix);
  const hay = texKey(product.name);
  if (!hay) return '';
  // Длинные названия проверяем раньше: «мягкое дерево» важнее, чем «дерево»
  const sorted = [...(textures || [])].sort((a, b) => (b.name || '').length - (a.name || '').length);
  const hit = sorted.find(t => {
    const k = texKey(t.name);
    return k.length >= 4 && hay.includes(k);
  });
  return hit ? String(hit.id) : '';
}


const Btn = ({ children, variant = 'primary', size = 'md', ...props }) => {
  const colors = { primary: 'var(--accent)', success: '#22c55e', danger: '#ef4444', warning: '#f59e0b', ghost: 'transparent' };
  const bg = colors[variant] || colors.primary;
  return (
    <button {...props} style={{
      background: props.disabled ? 'var(--card)' : bg,
      border: variant === 'ghost' ? '1px solid var(--border)' : 'none',
      borderRadius: 8, color: props.disabled ? 'var(--dim)' : (variant === 'ghost' ? 'var(--muted)' : '#fff'),
      padding: size === 'sm' ? '6px 12px' : '9px 18px', fontSize: 14, fontWeight: 600,
      cursor: props.disabled ? 'not-allowed' : 'pointer', ...props.style
    }}>{children}</button>
  );
};

// ─── Модальное окно ────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, width = 560 }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16
    }}>
      <div style={{ background: 'var(--card)', borderRadius: 16, width: '100%', maxWidth: width, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ color: 'var(--text)', fontWeight: 700, fontSize: 16 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

// ─── ВКЛАДКА: ЗАКАЗЫ ─────────────────────────────────────────────────────────
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingOrder, setEditingOrder] = useState(null);
  const role = sessionStorage.getItem('role');

  // Чьи заказы в списке. Менеджеру раньше показывали только его собственные,
  // и обойти это было нечем: заказ коллеги — в отпуске, на выходном, чей
  // клиент позвонил именно тебе — на экране просто не существовал.
  //
  // Теперь виден весь цех, а «только мои» — вот этот переключатель. Выбор
  // запоминается: кто работает один, включит его однажды и забудет.
  const [mine, setMine] = useState(
    () => localStorage.getItem('orders_mine') === '1');

  const load = useCallback(() => {
    setLoading(true);
    salesAPI.getOrders(mine ? { mine: 1 } : {})
      .then(r => setOrders(r.data)).finally(() => setLoading(false));
  }, [mine]);

  useEffect(() => { load(); }, [load]);

  const handleCloseOrder = async (order) => {
    if (!await confirmToast(
      `Закрыть заказ ${order.order_number}? Резерв сырья по нему снимется, заказ больше не будет ждать отгрузки.`,
      { confirmText: 'Закрыть', danger: true }
    )) return;
    try {
      const { data } = await salesAPI.closeOrder(order.id, '');
      toast.success(data.reservations_released
        ? `Заказ закрыт, снято резервов: ${data.reservations_released}`
        : 'Заказ закрыт');
      load();
      if (selectedOrder?.id === order.id) setSelectedOrder(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    }
  };

  const handleShip = async (order) => {
    if (!await confirmToast(`Заказ ${order.order_number} — отгрузить?`, { confirmText: 'Отгрузить' })) return;
    try {
      await salesAPI.changeOrderStatus(order.id, 'shipped');
      toast.success(t("Заказ отгружен"));
      load();
      if (selectedOrder?.id === order.id) setSelectedOrder(null);
    } catch (err) {
      toast.error(err.response?.data?.error || t("Ошибка"));
    }
  };

  // Фильтр по статусу был, поиска не было: сорок заказов, и нужный ищут
  // глазами по номеру или клиенту, прокручивая весь список.
  const filtered = orders.filter(o => {
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    // Ищем и по короткому коду: клиент звонит и называет то, что у него
    // на руках, а не номер заказа.
    return makeMatcher(orderSearch)(o.order_number, o.client_name, o.public_code);
  });

  const statuses = ['all', 'pending', 'stock', 'queue', 'production', 'ready', 'shipped', 'closed', 'cancelled'];
  const statusLabels = { all: 'Все', ...Object.fromEntries(Object.entries(STATUS_CFG).map(([k, v]) => [k, v.label])) };

  // Muddati o'tgan yoki yaqinlashgan buyurtmalar
  const urgentOrders = orders.filter(o =>
    !['shipped', 'cancelled'].includes(o.status) && getDeadlineInfo(o.deadline, o.status)
  );
  const overdueOrders = urgentOrders.filter(o => {
    const info = getDeadlineInfo(o.deadline, o.status);
    return info && info.label.startsWith('Просрочен');
  });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: selectedOrder ? '1fr 420px' : '1fr', gap: 20 }}>
      {/* Список заказов */}
      <div>
        {/* Срочные и просроченные */}
        {urgentOrders.length > 0 && (
          <div style={{
            background: overdueOrders.length > 0 ? '#ef444415' : '#f59e0b12',
            border: '1px solid ' + (overdueOrders.length > 0 ? '#ef444440' : '#f59e0b40'),
            borderRadius: 10, padding: '10px 14px', marginBottom: 12,
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 16 }}>{overdueOrders.length > 0 ? '🚨' : '⚠️'}</span>
            <span style={{ color: overdueOrders.length > 0 ? '#ef4444' : '#f59e0b', fontSize: 13, fontWeight: 700 }}>
              {overdueOrders.length > 0
                ? `${overdueOrders.length} просроченных заказа — требуют срочного внимания`
                : `${urgentOrders.length} заказа с приближающимся дедлайном`}
            </span>
            {overdueOrders.length === 0 && urgentOrders.length > 0 && (
              <button onClick={() => setStatusFilter('all')} style={{
                background: 'none', border: 'none', color: '#f59e0b',
                fontSize: 12, cursor: 'pointer', textDecoration: 'underline',
              }}>{t('Показать все')}</button>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <Btn onClick={() => setShowCreate(true)}>{t('+ Новый заказ')}</Btn>
          {/* Сорок заказов, и нужный ищут глазами по номеру или клиенту */}
          <input value={orderSearch} onChange={e => setOrderSearch(e.target.value)}
            placeholder={t('🔍 Номер заказа или клиент…')}
            style={{
              background: 'var(--input)', border: '1px solid var(--border)',
              borderRadius: 8, color: 'var(--text)', padding: '7px 12px',
              fontSize: 13, minWidth: 220, outline: 'none', fontFamily: 'inherit',
            }} />
          <Btn variant="ghost" size="sm" onClick={load}>{t('↻ Обновить')}</Btn>
          {/* Свои и чужие. Филиал при этом остаётся своим — «все» значит
              цех, а не завод целиком. */}
          <div style={{ display: 'flex', gap: 6 }}>
            {[[false, 'Все по филиалу'], [true, 'Только мои']].map(([v, label]) => (
              <button key={String(v)}
                onClick={() => {
                  setMine(v);
                  localStorage.setItem('orders_mine', v ? '1' : '0');
                }}
                style={{
                  padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                  background: mine === v ? 'var(--accent)' : 'var(--bg)',
                  border: '1px solid ' + (mine === v ? 'var(--accent)' : 'var(--border)'),
                  color: mine === v ? '#fff' : 'var(--muted)',
                }}>{t(label)}</button>
            ))}
          </div>
          <Btn variant="ghost" size="sm"
            onClick={() => exportAPI.orders(statusFilter !== 'all' ? { status: statusFilter } : {}).catch(() => toast.error(t("Ошибка экспорта")))}>
            📥 Экспорт Excel
          </Btn>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {statuses.map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} style={{
                padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: statusFilter === s ? (STATUS_CFG[s]?.color || '#64748b') : 'var(--bg)',
                border: '1px solid ' + (statusFilter === s ? (STATUS_CFG[s]?.color || '#64748b') : 'var(--border)'),
                color: statusFilter === s ? '#fff' : 'var(--muted)',
              }}>{statusLabels[s]} {s !== 'all' && `(${orders.filter(o => o.status === s).length})`}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <p style={{ color: 'var(--dim)', textAlign: 'center', padding: 40 }}>{t('Загрузка...')}</p>
        ) : filtered.length === 0 ? (
          <p style={{ color: 'var(--dim)', textAlign: 'center', padding: 40 }}>{t('Заказов нет')}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
            {filtered.map(order => {
              const isSelected = selectedOrder?.id === order.id;
              const dlInfo = getDeadlineInfo(order.deadline, order.status);
              // Заказ соседа. Смотреть можно — он и виден затем, чтобы
              // ответить его клиенту; править нельзя: заказ ведёт тот, кто
              // его принял. Сервер откажет в любом случае, но кнопка, которая
              // упирается в отказ, хуже отсутствующей.
              const foreign = role === 'manager' && order.is_mine === false;
              return (
                <div key={order.id}
                  onClick={() => setSelectedOrder(isSelected ? null : order)}
                  style={{
                    background: isSelected
                      ? 'var(--bg)'
                      : (dlInfo ? dlInfo.bg : 'var(--card)'),
                    border: '1px solid ' + (
                      isSelected ? 'var(--accent)' :
                      dlInfo ? dlInfo.color + '60' :
                      'var(--border)'
                    ),
                    borderRadius: 10, padding: '12px 16px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                  }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ color: 'var(--text)', fontWeight: 700, fontSize: 14 }}>{order.order_number}</span>
                      <Badge status={order.status} />
                      {order.is_service && <ServiceTag />}
                      {foreign && (
                        <span title={t('Заказ другого менеджера — только просмотр')}
                          style={{ background: 'var(--bg)', border: '1px solid var(--border)',
                                   borderRadius: 6, padding: '2px 8px', fontSize: 11,
                                   color: 'var(--muted)', fontWeight: 600 }}>
                          👤 {order.created_by_name}
                        </span>
                      )}
                      {dlInfo && (
                        <span style={{
                          background: dlInfo.color + '25', color: dlInfo.color,
                          borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                        }}>
                          {dlInfo.icon} {dlInfo.label}
                        </span>
                      )}
                    </div>
                    {/* Заказ приняли без остатка — предупреждение живёт на
                        самом заказе, а не гаснет тостом через три секунды.
                        Читать его будут через неделю, когда выяснится, что
                        позиция не вышла. */}
                    {order.stock_warning && (
                      <div style={{
                        background: '#f59e0b18', border: '1px solid #f59e0b40',
                        borderRadius: 8, padding: '7px 10px', margin: '2px 0 6px',
                        color: '#b45309', fontSize: 12, lineHeight: 1.45,
                      }}>
                        <b>⚠ {t('Принят без остатка')}</b>{' — '}
                        {t('может не выйти в срок. Уточните у технолога или '
                           + 'главного менеджера.')}
                        <div style={{ color: 'var(--muted)', marginTop: 3 }}>
                          {order.stock_warning}
                        </div>
                      </div>
                    )}
                    <div style={{ color: 'var(--muted)', fontSize: 13 }}>
                      {order.client_name} · {order.items_count} поз.
                      {order.deadline && (
                        <span style={{ color: dlInfo ? dlInfo.color : 'var(--dim)' }}>
                          {' · Срок: '}{new Date(order.deadline).toLocaleDateString('ru')}
                        </span>
                      )}
                      {order.status === 'ready' && (
                        <span style={{
                          marginLeft: 8, borderRadius: 6, padding: '2px 8px',
                          fontSize: 11, fontWeight: 700,
                          background: order.shipping_allowed ? '#22c55e18' : '#f59e0b18',
                          color: order.shipping_allowed ? '#22c55e' : '#f59e0b',
                        }}>
                          {order.shipping_allowed ? 'Отгрузка разрешена' : 'Ждёт разрешения'}
                        </span>
                      )}
                    </div>

                    {/* Сколько уже увезли. Отдали всё — заказ закрывается сам;
                        отдали часть — он остаётся открытым, и менеджеру нужно
                        видеть остаток, не раскрывая заказ. */}
                    {order.qty_shipped > 0 && order.status !== 'closed' && (
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          flex: '0 1 160px', height: 5, borderRadius: 3,
                          background: 'var(--border)', overflow: 'hidden',
                        }}>
                          <div style={{
                            width: Math.min(100, Math.round(order.qty_shipped / (order.qty_total || 1) * 100)) + '%',
                            height: '100%', background: '#22c55e',
                          }} />
                        </div>
                        <span style={{ color: 'var(--muted)', fontSize: 12 }}>
                          отгружено {Math.min(100, Math.round(order.qty_shipped / (order.qty_total || 1) * 100))}%
                          {' · '}{order.qty_shipped} из {order.qty_total} шт
                          {order.qty_total > order.qty_shipped && (
                            <b style={{ color: 'var(--warning)' }}>
                              {' · осталось '}{order.qty_total - order.qty_shipped}
                            </b>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                    {foreign && (
                      <span title={t('Заказ другого менеджера — только просмотр')}
                        style={{ fontSize: 13, color: 'var(--dim)' }}>👁</span>
                    )}
                    {!foreign && ['manager', 'admin'].includes(role) && !['shipped', 'cancelled'].includes(order.status) && (
                      order.assigned_to_press ? (
                        <span title={t("Технолог передал заказ на пресс — изменить нельзя")}
                          style={{ fontSize: 13, color: 'var(--dim)' }}>🔒</span>
                      ) : (
                        <button onClick={e => { e.stopPropagation(); setEditingOrder(order); }}
                          title={t("Изменить заказ")}
                          style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--muted)', width: 30, height: 30, cursor: 'pointer', fontSize: 14 }}>
                          ✏️
                        </button>
                      )
                    )}
                    {/* «Разрешить отгрузку» отсюда убрана. Разрешением стала
                        сама выписка распоряжения: раньше менеджер сначала
                        разрешал, потом отдельно писал накладную от руки — и
                        заказ, разрешённый без накладной, повисал между
                        кабинетом и складом. Теперь одно действие в «Отгрузке»:
                        кому, сколько чего, и склад видит документ.

                        Снять разрешение по-прежнему можно — распоряжение
                        отменяется там же, в списке выписанных. */}
                    {order.status === 'ready' && order.shipping_allowed && (
                      <span title={t('Отгрузка разрешена — распоряжение выписано')}
                        style={{ fontSize: 12, color: '#22c55e', fontWeight: 700,
                                 whiteSpace: 'nowrap' }}>✅ разрешено</span>
                    )}
                    {/* Склад грузит и без заказа, и сверх заказа — тогда заказ
                        остаётся открытым и держит резерв сырья под товар,
                        который никто уже не сделает. Резерв вычитается из
                        свободного остатка и мешает брать новые заказы. */}
                    {!foreign && !['shipped', 'cancelled', 'closed'].includes(order.status)
                      && ['manager', 'admin', 'director'].includes(role) && (
                      <Btn size="sm" variant="ghost"
                        title={t('Закрыть заказ и снять резерв сырья (товар увезли отдельной отгрузкой)')}
                        onClick={e => { e.stopPropagation(); handleCloseOrder(order); }}>
                        🔒 Закрыть
                      </Btn>
                    )}
                    {/* Отгружает склад: там экран с составом заказа и сканер.
                        У менеджера кнопка отправляла заказ целиком, набирая
                        паллеты по FIFO — что физически уехало, никто не сверял,
                        и это обесценивало QR на листах. Заодно она позволяла
                        менеджеру обойти собственное разрешение. Админу оставлена
                        как запасной ход: кладовщика может не быть на месте. */}
                    {order.status === 'ready' && role === 'admin' && (
                      <Btn size="sm" variant="success"
                        title={t('Запасной ход: отгрузить целиком, без сканирования')}
                        onClick={e => { e.stopPropagation(); handleShip(order); }}>
                        🚛 Отгрузить
                      </Btn>
                    )}
                    <span style={{ color: 'var(--dim)', fontSize: 12 }}>
                      {new Date(order.created_at).toLocaleDateString('ru')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Детали заказа */}
      {selectedOrder && (
        <OrderDetail order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}

      {/* Создать заказ */}
      {showCreate && (
        <NewOrderModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { load(); setShowCreate(false); }}
        />
      )}

      {editingOrder && (
        <EditOrderModal
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onSaved={() => { load(); setEditingOrder(null); }}
        />
      )}
    </div>
  );
}

// ─── Ссылка клиенту ──────────────────────────────────────────────────────────
//
// Раньше на вопрос «где мой заказ» отвечал менеджер: клиент звонил, менеджер
// шёл смотреть в программу или в цех. Теперь у клиента есть свой экран, и
// задача менеджера — один раз отдать на него ссылку.
//
// Даём и то, и другое: QR — навести телефоном прямо у стола, короткий код —
// продиктовать по телефону и написать на бланке. Ссылку теряют, шесть букв на
// бумажке доживают до отгрузки.
function ClientLinkModal({ order, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    salesAPI.getTrackingLink(order.id)
      .then(r => setData(r.data))
      .catch(() => setError(true));
  }, [order.id]);

  const copy = (text) => {
    // clipboard есть не везде (он требует защищённого соединения) — молча
    // ничего не делать нельзя, человек будет жать кнопку и не понимать.
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text)
        .then(() => toast.success(t('Скопировано')))
        .catch(() => toast.error(t('Не удалось скопировать')));
    } else {
      toast.error(t('Не удалось скопировать'));
    }
  };

  return (
    <Modal title={`Ссылка клиенту — ${order.order_number}`} onClose={onClose} width={460}>
      {error && (
        <div style={{ color: 'var(--danger)', fontSize: 14 }}>{t('Не удалось получить ссылку')}</div>
      )}
      {!data && !error && (
        <div style={{ color: 'var(--muted)', fontSize: 14 }}>{t('Загрузка')}…</div>
      )}
      {data && (
        <div style={{ textAlign: 'center' }}>
          {/* Белая подложка обязательна: в тёмной теме QR на тёмном фоне
              читается через раз, а сканеру нужен контраст. */}
          <div style={{ background: '#fff', display: 'inline-block',
                        padding: 10, borderRadius: 10 }}>
            <img src={data.qr} alt="QR" style={{ width: 190, height: 190, display: 'block' }} />
          </div>

          <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 16 }}>
            {t('Код заказа')}
          </div>
          <div style={{ color: 'var(--accent)', fontSize: 30, fontWeight: 700,
                        letterSpacing: 6, marginTop: 2 }}>
            {data.code}
          </div>

          <div style={{
            background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
            padding: '9px 12px', marginTop: 14, color: 'var(--muted)', fontSize: 12.5,
            wordBreak: 'break-all',
          }}>
            {data.link}
          </div>

          {/* Отправка одним нажатием.
              Копировать ссылку — это переключиться в мессенджер, найти
              клиента, вставить, дописать «здравствуйте». Двадцать раз в день.
              Здесь открывается сразу чат с этим клиентом и с готовым текстом:
              менеджеру остаётся нажать «отправить».

              Ни бота, ни платного API это не требует — wa.me и t.me открывают
              уже установленное приложение. Телефона в карточке нет — WhatsApp
              откроется без адресата, получателя выберут вручную, и это всё
              равно быстрее копирования. */}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <a
              href={`https://wa.me/${data.client_phone || ''}?text=${encodeURIComponent(data.message)}`}
              target="_blank" rel="noopener noreferrer"
              className="touch-btn"
              style={{
                flex: 1, background: '#25D366', borderRadius: 8, color: '#fff',
                padding: '11px 0', fontSize: 13.5, fontWeight: 700,
                textAlign: 'center', textDecoration: 'none',
              }}>
              WhatsApp
            </a>
            {/* Пока бот не настроен — просто делимся ссылкой через Telegram.
                Настроен — отдаём приглашение в бота: клиент нажмёт его один
                раз и дальше завод пишет ему сам. */}
            <a
              href={data.telegram
                ? `https://t.me/share/url?url=${encodeURIComponent(data.telegram)}`
                  + `&text=${encodeURIComponent('Заказ ' + order.order_number + ' — подпишитесь на уведомления')}`
                : `https://t.me/share/url?url=${encodeURIComponent(data.link)}`
                  + `&text=${encodeURIComponent('Ваш заказ ' + order.order_number + '. Код: ' + data.code)}`}
              target="_blank" rel="noopener noreferrer"
              className="touch-btn"
              style={{
                flex: 1, background: '#229ED9', borderRadius: 8, color: '#fff',
                padding: '11px 0', fontSize: 13.5, fontWeight: 700,
                textAlign: 'center', textDecoration: 'none',
              }}>
              Telegram
            </a>
          </div>

          {!data.client_phone && (
            <div style={{ color: 'var(--dim)', fontSize: 11.5, marginTop: 8,
                          lineHeight: 1.5 }}>
              У клиента в карточке не записан телефон — WhatsApp откроется
              без адресата, получателя выберите вручную.
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button onClick={() => copy(data.message)} className="touch-btn" style={{
              flex: 1, background: 'none', border: '1px solid var(--border)',
              borderRadius: 8, color: 'var(--muted)', padding: '10px 0',
              fontSize: 13, fontFamily: 'inherit',
            }}>
              📋 {t('Копировать текст')}
            </button>
            <button onClick={() => copy(data.code)} className="touch-btn" style={{
              background: 'none', border: '1px solid var(--border)', borderRadius: 8,
              color: 'var(--muted)', padding: '10px 16px', fontSize: 13,
              fontFamily: 'inherit',
            }}>
              {t('Код')}
            </button>
          </div>

          <div style={{ color: 'var(--dim)', fontSize: 11.5, marginTop: 14, lineHeight: 1.5 }}>
            На экране клиента нет ни имени, ни телефона, ни суммы — только
            номер, шаг и количество.
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── Услуга: плиту привозит заказчик ──────────────────────────────────────
//
// Плёнка и работа наши, плита — его. Со склада такой заказ не закрывается
// никогда: человек ждёт обратно СВОЮ плиту, а не такую же с нашего поддона.
const ServiceTag = () => (
  <span title="Плиту привозит заказчик — со склада уходит только плёнка"
    style={{
      background: '#8b5cf618', color: '#8b5cf6', border: '1px solid #8b5cf655',
      borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700,
      whiteSpace: 'nowrap',
    }}>🧾 УСЛУГА</span>
);

// ─── ДЕТАЛИ ЗАКАЗА ────────────────────────────────────────────────────────────
function OrderDetail({ order, onClose }) {
  const [detail, setDetail] = useState(null);
  const [pallets, setPallets] = useState([]);
  const [linkOpen, setLinkOpen] = useState(false);

  useEffect(() => {
    salesAPI.getOrder(order.id).then(r => setDetail(r.data));
    qualityAPI.getPallets({ order_id: order.id }).then(r => setPallets(r.data)).catch(() => {});
  }, [order.id]);

  return (
    <Card style={{ position: 'sticky', top: 0, maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ color: 'var(--text)', fontWeight: 700 }}>{order.order_number}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
        <Row label="Клиент" value={order.client_name} />
        <Row label="Статус" value={<Badge status={order.status} />} />
        {(detail?.is_service ?? order.is_service) && (
          <Row label="Тип" value={<ServiceTag />} />
        )}
        {order.deadline && (() => {
          const dlInfo = getDeadlineInfo(order.deadline, order.status);
          return (
            <Row label="Срок" value={
              <span style={{ color: dlInfo ? dlInfo.color : 'var(--text)', fontWeight: dlInfo ? 700 : 600 }}>
                {dlInfo && dlInfo.icon + ' '}
                {new Date(order.deadline).toLocaleDateString('ru')}
                {dlInfo && ` (${dlInfo.label})`}
              </span>
            } />
          );
        })()}
        <Row label="Создан" value={new Date(order.created_at).toLocaleDateString('ru')} />
        {detail?.created_by_name && <Row label="Создал" value={detail.created_by_name} />}
      </div>

      <button onClick={() => setLinkOpen(true)} className="touch-btn" style={{
        width: '100%', marginBottom: 16, background: 'none',
        border: '1px solid var(--border)', borderRadius: 8, color: 'var(--muted)',
        padding: '9px 0', fontSize: 13, fontFamily: 'inherit',
      }}>
        🔗 {t('Ссылка для клиента')}
      </button>
      {linkOpen && <ClientLinkModal order={order} onClose={() => setLinkOpen(false)} />}

      {detail && (
        <>
          <p style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 8, fontWeight: 600 }}>{t('ПОЗИЦИИ')}</p>
          {detail.items.map(item => (
            <div key={item.id} style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 12px', marginBottom: 6 }}>
              <div style={{ color: 'var(--text)', fontSize: 13, fontWeight: 600 }}>{item.product_name}</div>
              <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>
                {item.texture_name} · {item.quantity_done}/{item.quantity} шт
              </div>
              <div style={{ background: 'var(--card)', borderRadius: 4, height: 4, marginTop: 6 }}>
                <div style={{
                  width: `${Math.min(100, item.quantity > 0 ? (item.quantity_done / item.quantity) * 100 : 0)}%`,
                  height: 4, background: 'var(--accent)', borderRadius: 4,
                }} />
              </div>
            </div>
          ))}
        </>
      )}

      {/* Отгрузки по этому заказу. Менеджер смотрит в заказ и спрашивает:
          увезли или нет — раньше ответ приходилось искать в другом разделе.
          Заказ, просто закрытый вручную, сюда ничего не добавит: отгрузки по
          нему и не было. */}
      {(detail?.shipments || []).length > 0 && (
        <>
          <p style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 8,
                      fontWeight: 600, marginTop: 16 }}>
            ОТГРУЗКИ ({detail.shipments.length})
          </p>
          {detail.shipments.map(n => (
            <div key={n.id} style={{
              background: 'var(--bg)', borderRadius: 8, padding: '8px 12px',
              marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10,
              flexWrap: 'wrap', fontSize: 12.5,
            }}>
              <b style={{ color: 'var(--text)' }}>{n.number}</b>
              <span style={{
                color: n.status === 'done' ? '#22c55e'
                     : n.status === 'issued' ? '#f59e0b' : 'var(--dim)',
                fontWeight: 700,
              }}>{n.status_label}</span>
              <span style={{ color: 'var(--muted)' }}>{n.total} шт</span>
              <span style={{ marginLeft: 'auto', color: 'var(--dim)' }}>
                {n.by} · {new Date(n.done_at || n.created_at).toLocaleDateString('ru-RU')}
              </span>
              <button onClick={() => shipNoteAPI.print(n.id)}
                style={{ background: 'none', border: '1px solid var(--border)',
                         borderRadius: 6, color: 'var(--muted)', padding: '3px 9px',
                         fontSize: 11.5, cursor: 'pointer', fontFamily: 'inherit' }}>
                🖨
              </button>
            </div>
          ))}
        </>
      )}

      {pallets.length > 0 && (
        <>
          <p style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 8, fontWeight: 600, marginTop: 16 }}>
            ПАЛЛЕТЫ ({pallets.length})
          </p>
          {pallets.map(p => (
            <div key={p.id} style={{ background: 'var(--bg)', borderRadius: 8, padding: '8px 12px', marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <span style={{ color: 'var(--text)', fontSize: 12, fontWeight: 600 }}>{p.qr_short}</span>
                <span style={{ color: 'var(--muted)', fontSize: 12 }}> · {p.quantity} шт · </span>
                <span style={{
                  fontSize: 11, fontWeight: 600,
                  color: p.grade === '1' ? '#22c55e' : p.grade === '2' ? '#f59e0b' : '#ef4444',
                }}>
                  {p.grade_type_name || ({ '1': '1-й сорт', '2': '3-й сорт', defect: 'Брак' }[p.grade] || p.grade)}
                </span>
              </div>
              <span style={{ color: 'var(--dim)', fontSize: 12 }}>{p.location || '—'}</span>
            </div>
          ))}
        </>
      )}
    </Card>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ color: 'var(--muted)', fontSize: 13 }}>{label}</span>
      <span style={{ color: 'var(--text)', fontSize: 13, fontWeight: 600 }}>{value}</span>
    </div>
  );
}

// ─── НОВЫЙ ЗАКАЗ ──────────────────────────────────────────────────────────────
// Товара нет в списке — значит его нет и в 1С.
//
// Раньше менеджер заводил его прямо отсюда: клиент просит то, чего в каталоге
// нет, а ждать технолога некогда. Заказ принимался, лист выпускался — а потом
// бухгалтерия не могла выписать накладную: в 1С такой номенклатуры нет,
// продавать нечего. Расходилось всё уже на отгрузке, когда машина стоит под
// погрузкой.
//
// Теперь номенклатура заводится в 1С и приходит оттуда. Менеджеру остаётся
// сказать об этом тому, кто ведёт справочник, — и это честнее, чем кнопка,
// которая делает вид, что вопрос решён.
function ProductPickerModal({ products, textures, quantity, onPick, onCreated, onClose }) {
  const [q, setQ] = useState('');
  // Папки справочника 1С. Пока их не выгружают — колонки просто нет.
  const folders = useFolders('finished');
  const [folderId, setFolderId] = useState(null);
  // Новый товар прямо из формы заказа: клиент просит то, чего в каталоге нет,
  // и ждать технолога менеджер не может.
  // Кто вправе завести товар. Раньше это могла любая роль, открывшая форму
  // заказа; теперь — личное право, потому что заведённая здесь строка в 1С не
  // попадает. Когда обмен по номенклатуре заработает, галочку снимут, и форма
  // исчезнет сама.
  const mayAdd = sessionStorage.getItem('can_add_nom') === '1'
    || ['admin', 'director', 'technolog'].includes(sessionStorage.getItem('role'));
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nf, setNf] = useState({ name: '', code: '', unit: 'шт', thickness: '', matrix: '' });

  // «Только в наличии». В справочнике 1С почти семь тысяч позиций, а лежит на
  // складе три сотни: менеджер, которому надо отдать сегодня и со склада,
  // листает шесть тысяч строк «Нет» ради трёхсот нужных.
  //
  // Выбор запоминаем: тот, кто торгует остатком, включает его не на один
  // заказ, а насовсем. Чтобы включённый фильтр не выглядел пропажей товара,
  // кнопка горит зелёным, а счётчик рядом честно пишет «299 из 6803».
  const [onlyStock, setOnlyStock] = useState(
    () => localStorage.getItem('picker_only_stock') === '1');
  const toggleOnlyStock = () => setOnlyStock(v => {
    try { localStorage.setItem('picker_only_stock', v ? '0' : '1'); } catch (e) {}
    return !v;
  });

  const fmt = (n) => Number(n || 0).toLocaleString('ru');
  // Ищем по словам, а не по подстроке: «дуб 16» найдёт «ЛДСП 16мм Дуб Сонома».
  // Названия у всех в разном порядке, целиком их никто не помнит.
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  // Поиск идёт по ВСЕМУ справочнику, мимо выбранной папки: человек ищет
  // товар, а не место, где он лежит. Папка под строкой поиска покажет, где
  // он нашёлся.
  const inTree = words.length
    ? products
    : products.filter(p => inFolder(p, folderId, folders));
  const filtered = words.length
    ? inTree.filter(p => {
        const hay = `${p.code_1c || ''} ${p.code || ''} ${p.manufacturer || ''} ${p.name}`.toLowerCase();
        return words.every(w => hay.includes(w));
      })
    : inTree;
  // Фильтр по складу — последним: счётчик «сколько всего нашлось» должен
  // остаться, иначе непонятно, что именно спрятано.
  const listed = onlyStock
    ? filtered.filter(p => capacityOf(p).stock > 0)
    : filtered;
  const shown = listed.slice(0, 2000);

  const openCreate = () => {
    setNf({ name: q.trim(), code: '', unit: 'шт', thickness: '', matrix: '' });
    setCreating(true);
  };

  const submitNew = async () => {
    const name = nf.name.trim();
    if (!name) { toast.error(t('Введите название товара')); return; }
    setSaving(true);
    try {
      const { data } = await warehouseAPI.createNomenclature({
        name,
        code:      nf.code.trim(),
        type:      'finished',
        category:  'ldsp',
        unit:      nf.unit,
        thickness: nf.thickness ? Number(nf.thickness) : null,
        matrix:    nf.matrix ? Number(nf.matrix) : null,
        is_active: true,
      });
      // products-catalog отдаёт больше полей, чем справочник, — достраиваем,
      // чтобы новая строка считалась и рисовалась как остальные.
      onCreated({
        id: data.id, name: data.name, code: data.code || '', unit: data.unit,
        stock: 0, has_recipe: false, max_producible: null, materials: [],
      });
    } catch (err) {
      const d = err.response?.data;
      if (err.response?.status === 409 && d?.existing) {
        // Такой товар уже заведён — второй не нужен, берём существующий
        const inCatalog = products.find(p => p.id === d.existing.id);
        if (inCatalog) { toast(d.error); onPick(inCatalog); return; }
        // В каталоге его нет — значит категория не ЛДСП. Форма заказа показывает
        // только ЛДСП и ЛМДФ: плёнку и сырой ДСП завод не продаёт.
        toast.error(`${d.error}, но он не в списке ЛДСП — попросите технолога проверить категорию`);
        return;
      }
      toast.error(d?.error || (d?.name && d.name[0]) || 'Не удалось создать товар');
    } finally { setSaving(false); }
  };

  const rowStatus = (p) => {
    const c = capacityOf(p);
    if (c.stock >= quantity) {
      return { c: '#22c55e', label: `Склад: ${fmt(c.stock)} ${p.unit}` };
    }
    if (c.total >= quantity) {
      // Показываем не «сколько есть», а что ИМЕННО пойдёт в дело: столько-то
      // со склада, остальное сделаем.
      const make = quantity - c.stock;
      return { c: '#22c55e',
               label: c.stock > 0 ? `${fmt(c.stock)} склад + ${fmt(make)} сделать`
                                  : `Сделать: ${fmt(make)} ${p.unit}` };
    }
    if (c.total > 0) {
      return { c: '#f59e0b',
               label: `Мало: ${fmt(c.stock)} + ${fmt(c.prod)} = ${fmt(c.total)}` };
    }
    return { c: '#ef4444', label: `Нет (склад ${fmt(c.stock)}${p.has_recipe ? '' : ', без рецепта'})` };
  };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 }}>
      {/* Ширина была 760 px, и на неё приходилось дерево папок плюс строка
          товара. Названия ЛДСП длинные — «А - Гикори Шайн натуральный
          (синхропоры) ЛДСП, 2800*2070*16» — и обрезались ровно там, где
          начинается отличие: размер и толщина. Менеджер выбирал позицию по
          многоточию и промахивался.

          Теперь окно во всю ширину экрана: выбирают из 6817 позиций, и место
          здесь нужнее, чем аккуратная узкая карточка. */}
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'var(--card)', borderRadius: 16, width: '100%', maxWidth: 1680, height: '92vh', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ color: 'var(--text)', fontWeight: 700, fontSize: 16 }}>
            {creating ? t('Новый товар') : `Выберите товар · нужно ${fmt(quantity)} шт`}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        {creating ? (
        <div style={{ padding: '16px 22px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Inp label="Название *" autoFocus value={nf.name}
            onChange={e => setNf({ ...nf, name: e.target.value })}
            placeholder={t('ЛДСП 16мм Дуб Сонома')} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Inp label="Артикул" value={nf.code}
              onChange={e => setNf({ ...nf, code: e.target.value })} placeholder={t('Необязательно')} />
            <Sel label="Ед. изм." value={nf.unit} onChange={e => setNf({ ...nf, unit: e.target.value })}>
              <option value="шт">{t('шт')}</option>
              <option value="лист">{t('лист')}</option>
              <option value="м2">м²</option>
              <option value="кг">{t('кг')}</option>
            </Sel>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Inp label="Толщина, мм" type="number" step="0.5" min="0" value={nf.thickness}
              onChange={e => setNf({ ...nf, thickness: e.target.value })} placeholder={t('Необязательно')} />
            <Sel label="Матрица" value={nf.matrix} onChange={e => setNf({ ...nf, matrix: e.target.value })}>
              <option value="">{t('— не выбрана —')}</option>
              {(textures || []).map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
            </Sel>
          </div>
          <div style={{ background: '#f59e0b12', border: '1px solid #f59e0b30', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#f59e0b' }}>
            ⚠️ Рецепт и режимы прессования добавит технолог — пока товар считается «без рецепта»
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={submitNew} disabled={saving} style={{ flex: 1 }}>
              {saving ? 'Создание...' : '✅ Создать и выбрать'}
            </Btn>
            <Btn variant="ghost" onClick={() => setCreating(false)}>{t('← К списку')}</Btn>
          </div>
        </div>
        ) : (<>
        <div style={{ padding: '14px 22px 8px' }}>
          {/* Остаток в списке — из 1С. Встал обмен, и менеджер пообещает то,
              чего нет: числа-то на экране прежние. */}
          <StockFreshness style={{ marginBottom: 8 }} />
          <input autoFocus value={q} onChange={e => setQ(e.target.value)}
            placeholder={t("🔍 Поиск по названию или артикулу…")}
            style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', color: 'var(--text)', fontSize: 14 }} />
          <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11, color: 'var(--muted)', flexWrap: 'wrap' }}>
            <span><span style={{ color: '#22c55e' }}>●</span> {t('хватает')}</span>
            <span><span style={{ color: '#f59e0b' }}>●</span> {t('мало')}</span>
            <span><span style={{ color: '#ef4444' }}>●</span> {t('нет')}</span>
            <button onClick={toggleOnlyStock} style={{
              marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
              background: onlyStock ? '#22c55e18' : 'var(--bg)',
              border: `1px solid ${onlyStock ? '#22c55e' : 'var(--border)'}`,
              color: onlyStock ? '#22c55e' : 'var(--muted)',
              borderRadius: 20, padding: '4px 12px', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 11, fontWeight: onlyStock ? 700 : 400,
            }}>
              {onlyStock ? '☑' : '☐'} {t('только в наличии')}
            </button>
            <span>
              {onlyStock ? `${listed.length} из ${filtered.length}` : filtered.length} товаров
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, overflow: 'hidden',
                      padding: '4px 12px 16px' }}>
        {!q.trim() && (
          <CatalogTree folders={folders} value={folderId} onChange={setFolderId}
                       total={products.length}
                       noFolder={products.filter(p => !p.folder).length}
                       style={{ maxHeight: '100%' }} />
        )}
        <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
          {shown.map(p => {
            const s = rowStatus(p);
            return (
              <div key={p.id} onClick={() => onPick(p)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', borderLeft: `3px solid ${s.c}`, background: 'var(--bg)', marginBottom: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600, lineHeight: 1.35 }}>
                    {/* Код впереди названия: в 1С менеджер ищет им, и он в
                        пять раз короче — глазами цепляется первым. */}
                    {p.code_1c ? (
                      <span style={{ color: 'var(--muted)', fontWeight: 700,
                                     fontFamily: 'ui-monospace, monospace',
                                     fontSize: 12, marginRight: 8 }}>
                        {p.code_1c}
                      </span>
                    ) : null}
                    {p.name}
                    {p.manufacturer ? (
                      <span style={{ ...MAKER_ST, marginLeft: 8 }}>{p.manufacturer}</span>
                    ) : null}
                  </div>
                  {/* Путь показываем только в поиске: там строка вырвана из
                      дерева, и без него непонятно, откуда она. */}
                  {q.trim() && p.folder_path
                    ? <div style={{ color: 'var(--dim)', fontSize: 11 }}>{p.folder_path}</div>
                    : null}
                  {p.pending ? (
                    <div style={{ color: '#f59e0b', fontSize: 11, marginTop: 2 }}>
                      ⏳ {p.pending > 0 ? 'сделано, ждём 1С' : 'отгружено, ждём 1С'}:{' '}
                      {Math.abs(p.pending).toLocaleString('ru')} {p.unit}
                    </div>
                  ) : null}
                  {p.has_recipe && p.materials.length > 0 && (
                    <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {p.materials.map(mm => `${mm.material_name}: ${fmt(mm.available)} ${mm.unit}`).join(' · ')}
                    </div>
                  )}
                </div>
                <span style={{ flexShrink: 0, background: `${s.c}18`, color: s.c, borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700 }}>
                  {s.label}
                </span>
              </div>
            );
          })}
          {listed.length > shown.length && (
            <div style={{ color: 'var(--dim)', fontSize: 12, textAlign: 'center', padding: 10 }}>
              Показаны первые {shown.length} — уточните поиск…
            </div>
          )}
          {listed.length === 0 && (
            <div style={{ color: 'var(--dim)', fontSize: 13, textAlign: 'center', padding: 20 }}>
              {t('Ничего не найдено')}
              {/* Пусто из-за фильтра, а не потому, что товара нет в справочнике
                  — иначе менеджер идёт спрашивать, куда делась номенклатура. */}
              {onlyStock && filtered.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  Нашлось {filtered.length}, но склад по ним пуст —
                  снимите «только в наличии».
                </div>
              )}
            </div>
          )}
          {mayAdd && q.trim() && (
            <div onClick={openCreate}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 12px', borderRadius: 10, cursor: 'pointer', border: '1px dashed var(--border)', color: 'var(--accent)', fontSize: 13, fontWeight: 600, marginTop: 4 }}>
              ➕ Создать новый товар «{q.trim()}»
            </div>
          )}
        </div>
        </div>
        </>)}
      </div>
    </div>
  );
}

// ─── НОВЫЙ ЗАКАЗ ──────────────────────────────────────
// Новый заказ и правка заказа — ОДНА форма. Их было две, и они расходились
// при каждой доработке: «Услугу» добавили в новый заказ, а в правке её не
// было вовсе; производитель ДСП и упаковка стояли в разных местах; окно правки
// было вдвое уже. Менеджер открывал заказ на исправление и не узнавал форму,
// в которой его заводил.
function NewOrderModal({ onClose, onCreated }) {
  return <OrderFormModal onClose={onClose} onDone={onCreated} />;
}

function EditOrderModal({ order, onClose, onSaved }) {
  return <OrderFormModal order={order} onClose={onClose} onDone={onSaved} />;
}

function OrderFormModal({ order, onClose, onDone }) {
  const editing = !!order;
  const { branches, selectedBranch } = useBranch();
  // Настройка — того цеха, в котором заводится заказ, а не первого в списке.
  const curBranch = (branches || []).find(b => String(b.id) === String(selectedBranch))
    || ((branches || []).length === 1 ? branches[0] : null);
  const allowOrderWithoutStock = curBranch ? curBranch.allow_order_without_stock : true;
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [textures, setTextures] = useState([]);
  const [clientId, setClientId] = useState('');
  // Заводить нового контрагента предлагаем только после поиска: поля, висящие
  // под полем всегда, выглядели как обычный путь — и множили дубли.
  const [addingClient, setAddingClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientInn, setNewClientInn] = useState('');
  const [newClientType, setNewClientType] = useState('buyer');
  const [deadline, setDeadline] = useState(new Date().toISOString().split('T')[0]);
  const [note, setNote] = useState('');
  // Услуга: плиту привозит заказчик, наша только плёнка и работа.
  const [isService, setIsService] = useState(false);
  // dsp_maker и pack_type — условие заказа, а не решение цеха: клиент просит
  // ЧФМК, потому что у него так собирается мебель, и двойную упаковку, потому
  // что везти далеко. Оба поля до сих пор набирал технолог в заявке, по памяти
  // о разговоре, при котором его не было.
  const [items, setItems] = useState([
    { product: '', texture: '', quantity: 1, dsp_maker: '', pack_type: '' }]);
  const [pickerIdx, setPickerIdx] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [saving, setSaving] = useState(false);
  // Правка: пока заказ не приехал, форму не показываем — иначе менеджер
  // успевает начать набирать в пустые поля, и приехавший заказ их затирает.
  const [loadingOrder, setLoadingOrder] = useState(editing);
  useEffect(() => {
    if (!order) return;
    salesAPI.getOrder(order.id)
      .then(({ data: d }) => {
        setClientId(String(d.client || ''));
        setDeadline(d.deadline || '');
        setNote(d.note || '');
        setIsService(!!d.is_service);
        setItems((d.items || []).map(it => ({
          id: it.id,
          product: String(it.product || ''),
          texture: String(it.texture || ''),
          quantity: it.quantity,
          dsp_maker: it.dsp_maker || '',
          pack_type: it.pack_type || '',
        })));
      })
      .catch(() => toast.error(t("Ошибка загрузки заказа")))
      .finally(() => setLoadingOrder(false));
    // По номеру, а не по объекту: список заказов за окном обновляется сам, и
    // новый объект того же заказа перезагрузил бы форму поверх набранного.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order && order.id]);

  // Постоянные пожелания выбранного клиента.
  //
  // Производителя плиты и обвязку заказчик называет каждый раз одинаково, и
  // записаны они у него в карточке. Здесь они подставляются в позиции — но
  // остаются обычными полями: клиент может передумать на один раз, и спорить
  // с ним программа не должна.
  const chosen = clients.find(c => String(c.id) === String(clientId)) || null;
  const wish = {
    dsp_maker: (chosen && chosen.default_dsp_maker) || '',
    pack_type: (chosen && chosen.default_pack_type) || '',
  };
  const blankItem = () => ({ product: '', texture: '', quantity: 1,
    dsp_maker: wish.dsp_maker, pack_type: wish.pack_type });

  // Сменили клиента — переставляем подстановку. Трогаем только то, чего
  // менеджер не набирал сам: пустое либо оставшееся от прошлого клиента.
  // Набранное руками не перебиваем: это и есть тот случай, ради которого
  // поле осталось обычным.
  const prevWish = useRef({ dsp_maker: '', pack_type: '' });
  // В правке заказ приезжает уже заполненным, и пустое поле в нём — решение
  // менеджера, а не пропуск. Подставлять начинаем, только когда клиента
  // сменили руками.
  const autoWish = useRef(!editing);
  useEffect(() => {
    if (!autoWish.current) {
      prevWish.current = { dsp_maker: wish.dsp_maker, pack_type: wish.pack_type };
      return;
    }
    const was = prevWish.current;
    if (was.dsp_maker === wish.dsp_maker && was.pack_type === wish.pack_type) return;
    prevWish.current = { dsp_maker: wish.dsp_maker, pack_type: wish.pack_type };
    setItems(list => list.map(it => ({
      ...it,
      dsp_maker: (!it.dsp_maker || it.dsp_maker === was.dsp_maker)
        ? wish.dsp_maker : it.dsp_maker,
      pack_type: (!it.pack_type || it.pack_type === was.pack_type)
        ? wish.pack_type : it.pack_type,
    })));
  }, [wish.dsp_maker, wish.pack_type]);

  // Независимые запросы: медленный/упавший один не обнуляет остальные
  useEffect(() => {
    salesAPI.getClients('buyer')
      .then(r => setClients(r.data))
      .catch(() => toast.error(t("Список клиентов не загрузился")));
    warehouseAPI.getTextures()
      .then(r => setTextures(r.data))
      .catch(() => {});
    salesAPI.getProductsCatalog()
      .then(r => setProducts(r.data))
      .catch(() => toast.error(t("Каталог товаров не загрузился")))
      .finally(() => setLoadingProducts(false));
  }, []);

  const fmt = (n) => Number(n || 0).toLocaleString('ru');
  const prodById = (id) => products.find(p => String(p.id) === String(id));

  const itemStatus = (it) => {
    const p = prodById(it.product);
    const qty = Number(it.quantity) || 0;
    if (!p || !qty) return null;
    const c = capacityOf(p);
    if (c.stock >= qty) {
      return { ok: true, text: `✓ На складе: ${fmt(c.stock)} ${p.unit}` };
    }
    if (c.total >= qty) {
      const make = qty - c.stock;
      return { ok: true,
               text: c.stock > 0
                 ? `✓ ${fmt(c.stock)} со склада + ${fmt(make)} произвести`
                 : `✓ Произвести: ${fmt(make)} ${p.unit}` };
    }
    const parts = [`склад ${fmt(c.stock)}`];
    if (p.has_recipe) {
      parts.push(`сырья на ${p.max_producible == null ? '—' : fmt(c.prod)}`);
    }
    return { ok: false,
             text: `⚠ Не хватает: ${parts.join(' + ')} = ${fmt(c.total)}, нужно ${fmt(qty)}` };
  };

  const updateItem = (idx, field, value) =>
    setItems(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));

  // Товар и текстура выбираются вместе: текстура написана в самом названии
  // товара, доставать её из списка руками — лишний шаг и лишний повод ошибиться.
  // Не угадалась — поле остаётся пустым, менеджер выберет как раньше.
  const pickProduct = (idx, p) => setItems(items.map((it, i) => (
    i === idx
      ? { ...it, product: String(p.id), texture: guessTexture(p, textures) || it.texture }
      : it
  )));

  const handleSubmit = async () => {
    if (!clientId && !newClientName.trim()) { toast.error(t("Найдите контрагента или заведите нового")); return; }
    if (textures.length === 0) { toast.error(t("Нет активных текстур. Добавьте их в разделе «Технолог».")); return; }
    const bad = items.find(it => !it.product || !it.texture || !it.quantity);
    if (bad) { toast.error(t("Заполните продукт, текстуру и количество во всех позициях")); return; }
    setSaving(true);
    try {
      let finalClientId = clientId;
      if (!clientId && newClientName.trim()) {
        const { data: nc } = await salesAPI.createClient({
          name: newClientName.trim(), type: newClientType, inn_bin: newClientInn,
        });
        finalClientId = nc.id;
      }
      const body = {
        client: finalClientId,
        deadline: deadline || null,
        note,
        is_service: isService,
        items: items.map(it => ({
          // id — строка, которая уже есть в заказе: без него сервер
          // удалил бы её и завёл новую, а с ней и резерв, и очередь
          ...(it.id ? { id: it.id } : {}),
          product: it.product, texture: it.texture, quantity: Number(it.quantity),
          // Не отправить их — значит стереть: сервер пишет то, что пришло
          dsp_maker: (it.dsp_maker || '').trim(),
          pack_type: (it.pack_type || '').trim(),
        })),
      };
      if (editing) {
        await salesAPI.editOrder(order.id, body);
        toast.success(t("Заказ изменён"));
      } else {
        await salesAPI.createOrder(body);
        toast.success(t("Заказ создан и отправлен на согласование"));
      }
      onDone();
    } catch (err) {
      toast.error(errText(err, editing ? 'Ошибка сохранения' : 'Ошибка создания заказа'));
    } finally { setSaving(false); }
  };

  const shortages = items.filter(it => { const s = itemStatus(it); return s && !s.ok; });
  // Правку по нехватке не запрещаем: остаток уже зарезервирован под этот же
  // заказ, и счёт «хватает ли» видит его занятым — запрет не дал бы даже
  // уменьшить количество. Предупреждение остаётся.
  const isBlocked = !editing && !allowOrderWithoutStock && shortages.length > 0;

  return (
    // Позиция заказа стала пятью полями, и в шестьсот шестьдесят точек они
    // не встают: продукт сжимается до неузнаваемости, а название у ЛДСП —
    // это и есть вся суть строки.
    <Modal title={editing ? `${t('Изменить заказ')} ${order.order_number}` : t("Новый заказ")}
      onClose={onClose} width={1120}>
      {loadingOrder ? (
        <p style={{ color: 'var(--dim)', textAlign: 'center', padding: 30 }}>{t('Загрузка…')}</p>
      ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <ClientPicker label="Клиент" clients={clients} value={clientId} searchType="buyer"
          onChange={(v) => { autoWish.current = true; setClientId(v); if (v) { setAddingClient(false); setNewClientName(''); } }}
          onCreateNew={(q) => { setAddingClient(true); setNewClientName(q); }} />
        {addingClient && !clientId && (
          <div style={{ border: '1px solid var(--accent)', borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ color: 'var(--accent)', fontSize: 12, fontWeight: 700, flex: 1 }}>НОВЫЙ КОНТРАГЕНТ</span>
              <button type="button" onClick={() => { setAddingClient(false); setNewClientName(''); setNewClientInn(''); }}
                style={{ background: 'none', border: 'none', color: 'var(--dim)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
              <Inp label="Название" value={newClientName}
                onChange={e => setNewClientName(e.target.value)} placeholder={t("ООО «Компания»")} />
              {/* ИНН здесь не формальность: 1С по нему находит контрагента сама,
                  без него каждая отгрузка ждёт ручного сопоставления */}
              <Inp label="ИНН / БИН" value={newClientInn} inputMode="numeric"
                onChange={e => setNewClientInn(e.target.value.replace(/\D/g, '').slice(0, 12))}
                placeholder={t("необязательно")} />
              <Sel label="Тип" value={newClientType} onChange={e => setNewClientType(e.target.value)}>
                <option value="buyer">{t('Покупатель')}</option>
                <option value="both">{t('Покупатель+Поставщик')}</option>
              </Sel>
            </div>
          </div>
        )}
        {(wish.dsp_maker || wish.pack_type) && (
          <div style={{ background: 'var(--accent-weak)', border: '1px solid var(--border)',
                        borderRadius: 8, padding: '8px 12px', fontSize: 12,
                        color: 'var(--muted)' }}>
            Этот заказчик обычно просит{' '}
            <b style={{ color: 'var(--text)' }}>
              {[wish.dsp_maker, wish.pack_type].filter(Boolean).join(' · ')}
            </b>{' '}— подставлено в позиции. В строке это можно поправить или стереть.
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Inp label="Срок сдачи" type="date" value={deadline} onChange={e => setDeadline(e.target.value)} />
          <Inp label="Примечание" value={note} onChange={e => setNote(e.target.value)} placeholder={t("Необязательно")} />
        </div>

        {/* Услуга. Спрашиваем здесь, при приёме, потому что дальше об этом
            узнать негде: у пресса стоит человек, которому надо взять плиту
            из привезённой стопки, а не со своего поддона, и решает он это
            за минуту до работы. */}
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
          background: isService ? '#8b5cf612' : 'var(--bg)',
          border: `1px solid ${isService ? '#8b5cf655' : 'var(--border)'}`,
          borderRadius: 10, padding: '11px 14px',
        }}>
          <input type="checkbox" checked={isService}
            onChange={e => setIsService(e.target.checked)}
            style={{ width: 18, height: 18, marginTop: 1, flexShrink: 0,
                     accentColor: '#8b5cf6', cursor: 'pointer' }} />
          <span>
            <b style={{ color: 'var(--text)', fontSize: 14 }}>
              🧾 Услуга — плиту привозит заказчик
            </b>
            <span style={{ display: 'block', color: 'var(--muted)',
                           fontSize: 12.5, marginTop: 2 }}>
              Со склада уйдёт только плёнка. Плиту не списываем и со склада
              такой заказ не закрываем: человек ждёт обратно свою.
            </span>
          </span>
        </label>

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
          <p style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 10, fontWeight: 600 }}>{t('ПОЗИЦИИ ЗАКАЗА')}</p>
          {items.map((it, idx) => {
            const p = prodById(it.product);
            const st = itemStatus(it);
            return (
              <div key={idx} style={{ marginBottom: 12 }}>
                {/* Пять полей одной строкой. Раньше «Производитель ДСП» и «Тип
                    упаковки» стояли отдельной строкой под остальными, и одна
                    позиция занимала два ряда: глазом это читалось как две
                    позиции, а на пяти строках заказа окно уходило за экран. */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <div style={{ flex: 2.6, minWidth: 0 }}>
                    {idx === 0 && <label style={{ color: 'var(--muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>{t('Продукт')}</label>}
                    <button type="button" onClick={() => setPickerIdx(idx)}
                      style={{
                        width: '100%', textAlign: 'left', background: 'var(--bg)',
                        border: '1px solid var(--border)', borderRadius: 8, padding: '9px 12px',
                        color: p ? 'var(--text)' : 'var(--dim)', cursor: 'pointer', fontSize: 13,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                      {p ? p.name : (loadingProducts ? 'Загрузка каталога…' : '📋 Выбрать товар из списка')}
                    </button>
                  </div>
                  <div style={{ flex: 1.2, minWidth: 0 }}>
                    <Sel label={idx === 0 ? 'Текстура' : undefined}
                      value={it.texture} onChange={e => updateItem(idx, 'texture', e.target.value)}>
                      <option value="">{t('— текстура —')}</option>
                      {textures.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </Sel>
                  </div>
                  {/* Необязательные: чаще всего клиенту всё равно, чья плита и
                      как её обвязать, и требовать заполнения значило бы
                      заставлять выдумывать. Названо — поедет в заявку и
                      обратно уже не правится. */}
                  <div style={{ flex: 1.2, minWidth: 0 }}>
                    <SuggestInput size="md" kind={KIND_DSP}
                      label={idx === 0 ? 'Производитель ДСП' : undefined}
                      value={it.dsp_maker}
                      onChange={v => updateItem(idx, 'dsp_maker', v)}
                      placeholder={t('необязательно')} />
                  </div>
                  <div style={{ flex: 1.2, minWidth: 0 }}>
                    <SuggestInput size="md" kind={KIND_PACK}
                      label={idx === 0 ? 'Тип упаковки' : undefined}
                      value={it.pack_type}
                      onChange={v => updateItem(idx, 'pack_type', v)}
                      placeholder={t('необязательно')} />
                  </div>
                  <div style={{ width: 88 }}>
                    <Inp label={idx === 0 ? 'Кол-во' : undefined} type="number" min={1}
                      value={it.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} />
                  </div>
                  {items.length > 1 && (
                    <button onClick={() => setItems(items.filter((_, i) => i !== idx))}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 18, paddingBottom: 4 }}>🗑</button>
                  )}
                </div>
                {st && (
                  <div style={{ marginTop: 5, fontSize: 12, fontWeight: 600, color: st.ok ? '#22c55e' : '#ef4444' }}>
                    {st.text}
                  </div>
                )}
              </div>
            );
          })}
          <Btn variant="ghost" size="sm" onClick={() => setItems([...items, blankItem()])}>
            + Добавить позицию
          </Btn>
        </div>

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {isBlocked && (
            <div style={{ background: '#ef444412', border: '1px solid #ef444430', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#ef4444' }}>
              ⚠️ Недостаточно товара/сырья по некоторым позициям
            </div>
          )}
          {!isBlocked && shortages.length > 0 && (
            <div style={{ background: '#f59e0b12', border: '1px solid #f59e0b30', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#f59e0b' }}>
              ⚠️ Часть позиций не обеспечена складом — заказ создать можно, срок может увеличиться
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={handleSubmit} disabled={saving || isBlocked} style={{ flex: 1 }}>
              {saving ? (editing ? 'Сохранение…' : 'Создание...')
                : isBlocked ? '🚫 Недостаточно'
                : editing ? '✅ Сохранить изменения' : '✅ Создать заказ'}
            </Btn>
            <Btn variant="ghost" onClick={onClose}>{t('Отмена')}</Btn>
          </div>
        </div>
      </div>
      )}

      {pickerIdx !== null && (
        <ProductPickerModal
          products={products}
          textures={textures}
          quantity={Number(items[pickerIdx] && items[pickerIdx].quantity) || 1}
          onClose={() => setPickerIdx(null)}
          onCreated={(p) => {
            // Каталог отдаётся отсортированным по названию — держим тот же порядок
            setProducts(prev => [...prev, p].sort((a, b) => a.name.localeCompare(b.name, 'ru')));
            pickProduct(pickerIdx, p);
            setPickerIdx(null);
          }}
          onPick={(p) => { pickProduct(pickerIdx, p); setPickerIdx(null); }}
        />
      )}
    </Modal>
  );
}

// ─── ВКЛАДКА: СКЛАД СЫРЬЯ ─────────────────────────────────────────────────────
// Значок завода-изготовителя: приглушённый, отдельно от названия и кода.
const MAKER_ST = {
  background: 'var(--card)', border: '1px solid var(--border)',
  borderRadius: 5, padding: '1px 7px', fontSize: 11,
  color: 'var(--muted)', whiteSpace: 'nowrap',
};

function StockItemCard({ item, editMinId, editVal, setEditMinId, setEditVal, saveMin }) {
  const isLow     = item.is_low;
  // Минус — не «мало осталось», а ошибка учёта, и выглядеть должен иначе.
  // Раньше он вообще не подсвечивался: обе ветки is_low требуют total > 0,
  // а полоса при этом красилась зелёным, потому что -20 / -20 = 1.
  const isNeg     = item.is_negative;
  const bc        = (isLow || isNeg) ? '#ef4444'
                  : item.available_quantity / (item.total_quantity || 1) > 0.5 ? '#22c55e' : '#f59e0b';
  const pct       = isNeg ? 100
                  : item.total_quantity > 0
                    ? Math.min(100, Math.round((item.available_quantity / item.total_quantity) * 100)) : 0;
  const isEditing = editMinId === item.id;
  return (
    <div style={{
      background: isNeg ? '#ef444420' : isLow ? '#ef444410' : 'var(--bg)',
      border: (isNeg ? '2px solid #ef4444' : '1px solid ' + (isLow ? '#ef444430' : 'transparent')),
      borderRadius: 8, padding: '10px 14px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isNeg ? <span style={{ fontSize: 14 }}>⛔</span> : isLow && <span style={{ fontSize: 14 }}>🔴</span>}
          {item.code_1c ? (
            <span style={{ color: 'var(--dim)', fontFamily: 'ui-monospace, monospace',
                           fontSize: 11.5, fontWeight: 700 }}>
              {item.code_1c}
            </span>
          ) : null}
          <span style={{
            color: (isLow || isNeg) ? '#ef4444' : 'var(--text)',
            fontSize: 14, fontWeight: (isLow || isNeg) ? 700 : 400,
          }}>{item.name}</span>
          {/* Завод — своим значком, а не в столбце кода: там он ломал строй и
              притворялся кодом. Одна и та же плита от разных заводов — разные
              позиции, и различают их именно этим. */}
          {item.manufacturer ? (
            <span style={MAKER_ST}>{item.manufacturer}</span>
          ) : null}
          {isNeg && (
            <span style={{
              background: '#ef4444', color: '#fff', borderRadius: 4,
              padding: '1px 7px', fontSize: 10, fontWeight: 800,
            }}>{t('МИНУС')}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, fontSize: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--dim)' }}>
            Всего: <b style={{ color: isNeg ? '#ef4444' : 'var(--muted)' }}>{item.total_quantity.toLocaleString()} {item.unit}</b>
          </span>
          <span style={{ color: 'var(--dim)' }}>
            Резерв: <b style={{ color: '#f59e0b' }}>{item.reserved_quantity.toLocaleString()} {item.unit}</b>
          </span>
          <span style={{ color: 'var(--dim)' }}>
            Свободно: <b style={{ color: bc }}>{item.available_quantity.toLocaleString()} {item.unit}</b>
          </span>
          {/* Отправлено в 1С, но ею ещё не проведено. Это не остаток: обещать
              нельзя, а видеть надо — иначе склад решит, что выпуск потерялся. */}
          {item.pending ? (
            <span style={{ color: '#f59e0b' }}>
              ⏳ {item.pending > 0 ? 'ждёт 1С' : 'отгружено, ждёт 1С'}:{' '}
              <b>{Math.abs(item.pending).toLocaleString()} {item.unit}</b>
            </span>
          ) : null}
          {isEditing ? (
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <span style={{ color: 'var(--dim)', fontSize: 11 }}>{t('мин:')}</span>
              <input
                type="number" value={editVal}
                onChange={e => setEditVal(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveMin(item.id)}
                autoFocus
                style={{
                  width: 80, padding: '2px 6px', background: 'var(--input)',
                  border: '1px solid var(--accent)', borderRadius: 4,
                  color: 'var(--text)', fontSize: 12, outline: 'none',
                }}
              />
              <button onClick={() => saveMin(item.id)} style={{ background: '#22c55e', border: 'none', borderRadius: 4, color: '#fff', padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>✓</button>
              <button onClick={() => setEditMinId(null)} style={{ background: 'none', border: 'none', color: 'var(--dim)', cursor: 'pointer', fontSize: 13 }}>✕</button>
            </div>
          ) : (
            <button
              onClick={() => { setEditMinId(item.id); setEditVal(item.min_stock || ''); }}
              style={{
                background: isLow ? '#ef444420' : 'var(--card)',
                border: '1px solid ' + (isLow ? '#ef444440' : 'var(--border)'),
                borderRadius: 4, color: isLow ? '#ef4444' : 'var(--dim)',
                padding: '2px 8px', fontSize: 11, cursor: 'pointer',
              }}
            >
              мин: {item.min_stock > 0 ? item.min_stock.toLocaleString() : '—'}
            </button>
          )}
        </div>
      </div>
      <div style={{ background: 'var(--card)', borderRadius: 4, height: 5 }}>
        <div style={{ width: `${pct}%`, height: 5, background: bc, borderRadius: 4, transition: 'width 0.3s' }} />
      </div>
      {isLow && item.min_stock > 0 && (
        <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>
          Нужно закупить: {Math.ceil(item.min_stock - item.available_quantity).toLocaleString()} {item.unit}
        </div>
      )}
    </div>
  );
}

function StockTab() {
  const [stock, setStock]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editMinId, setEditMinId] = useState(null);
  const [editVal, setEditVal]     = useState('');
  // Роль решает, что показывать сверху: менеджеру — только остаток, без
  // чужих задач. Экран открывают двое, менеджер и администратор.
  const isManager = sessionStorage.getItem('role') === 'manager';

  // Шапка экрана прилипает к верху, и колонка папок должна вставать ровно под
  // ней. Высоту не зашиваем числом: на узком экране фильтры переносятся на
  // второй ряд, и зашитое число оставило бы дырку или наползание.
  const headRef = React.useRef(null);
  const [headH, setHeadH] = useState(0);
  useEffect(() => {
    const el = headRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setHeadH(el.offsetHeight));
    ro.observe(el);
    setHeadH(el.offsetHeight);
    return () => ro.disconnect();
  }, []);
  // 58 — высота общей шапки страницы, она тоже прилипшая.
  const TOP = 58;
  const treeTop = TOP + headH + 6;
  // Папки справочника 1С; пока их не выгружают — дерево строится из категорий.
  const folders = useFolders();
  const [folderId, setFolderId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Позиций больше пятисот, и отличаются они текстурой, размером и толщиной —
  // одним поиском по названию проблемное место не найти.
  const [flt, setFlt] = useState({ texture: '', size: '', thickness: '',
                                   maker: '',
                                   minusOnly: false, inStockOnly: false });
  // Список критического запаса — 300+ позиций: развёрнутым он занимал весь
  // экран, и до самих остатков приходилось долго скроллить. Свёрнут по
  // умолчанию, счётчик виден всегда.
  const [showLow, setShowLow] = useState(false);

  const load = () => Promise.all([
    warehouseAPI.getStockSummary(),
    warehouseAPI.getStockSummary({ type: 'finished' })
  ])
    .then(([raw, finished]) => setStock([...raw.data, ...finished.data]))
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const saveMin = async (id) => {
    try {
      await warehouseAPI.setMinStock(id, editVal);
      toast.success(t("Минимальный запас сохранён"));
      setEditMinId(null);
      load();
    } catch { toast.error(t("Ошибка")); }
  };

  const filteredStock = stock.filter(i => {
    if (!makeMatcher(searchQuery)(i.name, i.code, i.code_1c, i.manufacturer)) return false;
    if (flt.maker && i.manufacturer !== flt.maker)       return false;
    if (flt.texture   && i.texture   !== flt.texture)   return false;
    if (flt.size      && i.size      !== flt.size)      return false;
    if (flt.thickness && i.thickness !== flt.thickness) return false;
    if (flt.minusOnly && !i.is_negative)                return false;
    // «Что есть» — обратный вопрос к «что в минусе», и задают его чаще:
    // в справочнике больше тысячи позиций, почти все с нулём.
    //
    // Поля `quantity` в сводке склада нет и не было: сервер отдаёт
    // total_quantity (лежит на складе) и available_quantity (свободно, за
    // вычетом брони). Number(undefined) — NaN, любое сравнение с ним ложно,
    // и кнопка вычищала список до нуля вместо того, чтобы его сузить.
    //
    // Считаем по тому, что ЛЕЖИТ: позиция, целиком забронированная под чужой
    // заказ, со склада никуда не делась — её ищут глазами так же.
    if (flt.inStockOnly && !(Number(i.total_quantity) > 0)) return false;
    return true;
  });

  // Дерево: из 1С, а пока его нет — из категорий. Поиск идёт мимо дерева:
  // человек ищет позицию, а не место, где она лежит.
  const tree = treeOf(folders, filteredStock);
  const shownStock = searchQuery
    ? filteredStock
    : filteredStock.filter(i => inFolder(i, folderId, tree));

  const lowItems   = stock.filter(i => i.is_low);
  const minusItems = stock.filter(i => i.is_negative);

  // Варианты для выпадающих — из того, что реально есть на складе
  const optsOf = (key) => [...new Set(stock.map(i => i[key]).filter(Boolean))].sort();
  const fltStyle = {
    background: 'var(--input)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text)', padding: '6px 10px', fontSize: 13,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Минус на складе — это не «мало осталось», а ошибка учёта: товар выдали,
          прихода нет.

          Менеджеру это не показываем. Он смотрит сюда, чтобы понять, из чего
          можно взять заказ, а починить расхождение учёта не может: приход
          вносит склад, а разбирается бухгалтерия. Красная полоса во весь экран
          при этом заслоняла то, за чем он пришёл. */}
      {!isManager && minusItems.length > 0 && (
        <div
          onClick={() => setFlt(f => ({ ...f, minusOnly: !f.minusOnly }))}
          style={{
            background: '#ef444418', border: '2px solid #ef4444', borderRadius: 10,
            padding: '12px 16px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}>
          <span style={{
            background: '#ef4444', color: '#fff', borderRadius: 20,
            padding: '2px 12px', fontSize: 17, fontWeight: 900,
          }}>{minusItems.length}</span>
          <span style={{ color: '#ef4444', fontSize: 15, fontWeight: 800 }}>
            {t('позиций с МИНУСОВЫМ остатком')}
          </span>
          <span style={{ color: 'var(--muted)', fontSize: 12 }}>
            {t('товар выдали, а прихода нет — учёт разошёлся')}
          </span>
          <span style={{ color: flt.minusOnly ? '#ef4444' : 'var(--dim)', fontSize: 12, marginLeft: 'auto', fontWeight: 700 }}>
            {flt.minusOnly ? t('✓ показаны только они') : t('показать только их')}
          </span>
        </div>
      )}

      {/* Заявка на закупку — тоже не его дело: заказывает сырьё снабжение,
          а не продажи. У директора этот же список есть на дашборде. */}
      {!isManager && lowItems.length > 0 && (
        <div style={{ background: '#ef444412', border: '1px solid #ef444440', borderRadius: 10, padding: '12px 16px' }}>
          <div style={{
            color: '#ef4444', fontWeight: 700, fontSize: 14, marginBottom: 8,
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          }}>
            <span style={{ flex: 1 }}>
              ⚠️ Критический запас — {lowItems.length} позиций требуют закупки
            </span>
            {/* Список нужен снабженцу на бумаге: с ним идут к поставщику */}
            <button
              onClick={() => printLowStock(lowItems)}
              style={{
                background: '#ef4444', border: 'none',
                borderRadius: 8, color: '#fff', padding: '4px 12px',
                fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
              🖨 Печать заявки
            </button>
            <button
              onClick={() => setShowLow(v => !v)}
              style={{
                background: 'transparent', border: '1px solid #ef444460',
                borderRadius: 8, color: '#ef4444', padding: '4px 12px',
                fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
              {showLow ? `▲ ${t('свернуть')}` : `▼ ${t('показать список')}`}
            </button>
          </div>
          {/* Даже развёрнутый список не должен выталкивать остатки за экран —
              своя полоса прокрутки внутри блока */}
          {showLow && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 8,
              maxHeight: '38vh', overflowY: 'auto', paddingRight: 4,
            }}>
              {lowItems.map(item => (
                <span key={item.id} style={{ background: '#ef444420', color: '#ef4444', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                  {item.name}: {item.available_quantity.toLocaleString()} {item.unit}
                  {item.min_stock > 0 && ` / мин. ${item.min_stock.toLocaleString()}`}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Фильтры и заголовок со строкой поиска — одним прилипшим блоком.
          Позиций больше тысячи; прокрутив до середины, человек терял и поиск,
          и фильтры, и ему приходилось возвращаться наверх ради одного щелчка. */}
      <div ref={headRef} style={{
        position: 'sticky', top: TOP, zIndex: 30,
        background: 'var(--bg)', paddingTop: 10, paddingBottom: 10,
        display: 'flex', flexDirection: 'column', gap: 10,
        marginBottom: -6,
      }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={fltStyle} value={flt.texture}
          onChange={e => setFlt(f => ({ ...f, texture: e.target.value }))}>
          <option value="">{t('Текстура — все')}</option>
          {optsOf('texture').map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select style={fltStyle} value={flt.size}
          onChange={e => setFlt(f => ({ ...f, size: e.target.value }))}>
          <option value="">{t('Размер — все')}</option>
          {optsOf('size').map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select style={fltStyle} value={flt.thickness}
          onChange={e => setFlt(f => ({ ...f, thickness: e.target.value }))}>
          <option value="">{t('Толщина — все')}</option>
          {optsOf('thickness').map(v => <option key={v} value={v}>{v} мм</option>)}
        </select>
        {/* Завод: одна и та же плита от разных заводов лежит разными позициями,
            и разбираются в них именно по нему. */}
        {optsOf('manufacturer').length > 0 && (
          <select style={fltStyle} value={flt.maker}
            onChange={e => setFlt(f => ({ ...f, maker: e.target.value }))}>
            <option value="">{t('Производитель — все')}</option>
            {optsOf('manufacturer').map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        )}
        <button
          onClick={() => setFlt(f => ({ ...f, minusOnly: !f.minusOnly }))}
          style={{
            ...fltStyle, cursor: 'pointer', fontWeight: 700,
            background: flt.minusOnly ? '#ef4444' : 'var(--input)',
            borderColor: flt.minusOnly ? '#ef4444' : 'var(--border)',
            color: flt.minusOnly ? '#fff' : 'var(--muted)',
          }}>
          ⛔ {t('только минус')}
        </button>
        {/* Чаще всего нужен обратный вопрос: что вообще ЕСТЬ. В справочнике
            больше тысячи позиций, и почти все с нулём — они прокручиваются
            мимо, пока ищешь ту, что лежит на складе. */}
        <button
          onClick={() => setFlt(f => ({ ...f, inStockOnly: !f.inStockOnly }))}
          style={{
            ...fltStyle, cursor: 'pointer', fontWeight: 700,
            background: flt.inStockOnly ? '#22c55e' : 'var(--input)',
            borderColor: flt.inStockOnly ? '#22c55e' : 'var(--border)',
            color: flt.inStockOnly ? '#fff' : 'var(--muted)',
          }}>
          ✅ {t('только с остатком')}
        </button>
        {(flt.texture || flt.size || flt.thickness || flt.minusOnly || flt.inStockOnly) && (
          <>
            <button
              onClick={() => setFlt({ texture: '', size: '', thickness: '',
                                      minusOnly: false, inStockOnly: false })}
              style={{ ...fltStyle, cursor: 'pointer', color: 'var(--dim)' }}>
              {t('Сбросить')}
            </button>
            <span style={{ color: 'var(--dim)', fontSize: 12 }}>
              {t('найдено')}: <b style={{ color: 'var(--text)' }}>{filteredStock.length}</b>
            </span>
          </>
        )}
      </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <h2 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 700, margin: 0 }}>
            {t('📦 Остатки сырья на складе')}
            <span style={{ color: 'var(--dim)', fontSize: 12.5, fontWeight: 500, marginLeft: 8 }}>
              {shownStock.length} из {filteredStock.length}
            </span>
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--dim)', fontSize: 12 }}>{t('Нажмите «min» чтобы задать минимальный запас')}</span>
            <div style={{ position: 'relative' }}>
              <input 
                type="text" 
                placeholder={t("Поиск номенклатуры...")} 
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{
                  padding: '8px 12px 8px 30px', 
                  borderRadius: 20, 
                  border: '1px solid var(--border)',
                  background: 'var(--input)',
                  color: 'var(--text)',
                  fontSize: 13,
                  outline: 'none',
                  width: 220
                }}
              />
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14 }}>🔍</span>
            </div>
          </div>
        </div>
      </div>

      <Card>
        {loading ? (
          <p style={{ color: 'var(--dim)', textAlign: 'center', padding: 30 }}>{t('Загрузка...')}</p>
        ) : stock.length === 0 ? (
          <p style={{ color: 'var(--dim)', textAlign: 'center', padding: 30 }}>{t('Нет данных')}</p>
        ) : filteredStock.length === 0 ? (
          <p style={{ color: 'var(--dim)', textAlign: 'center', padding: 30 }}>{t('По вашему запросу ничего не найдено')}</p>
        ) : (
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            {/* Слева дерево, справа один список.
                Раньше здесь был аккордеон из трёх категорий, и «Бумага /
                Плёнка» разворачивалась в 258 строк подряд — их листали
                глазами, потому что свернуть можно было только всю категорию
                целиком. Дерево делает то же самое, но одним щелчком и на любую
                глубину; когда 1С пришлёт свои папки, оно станет ими само. */}
            {!searchQuery && (
              <CatalogTree folders={tree} value={folderId} onChange={setFolderId}
                           total={filteredStock.length}
                           noFolder={filteredStock.filter(i => !i.folder).length}
                           style={{ position: 'sticky', top: treeTop,
                                    maxHeight: `calc(100vh - ${treeTop + 24}px)` }} />
            )}
            <div style={{ flex: 1, minWidth: 0, display: 'flex',
                          flexDirection: 'column', gap: 8 }}>
              {shownStock.length === 0 ? (
                <p style={{ color: 'var(--dim)', fontSize: 13, textAlign: 'center', padding: '10px 0' }}>
                  Здесь пусто
                </p>
              ) : shownStock.map(item => (
                <StockItemCard
                  key={item.id} item={item}
                  editMinId={editMinId} editVal={editVal}
                  setEditMinId={setEditMinId} setEditVal={setEditVal}
                  saveMin={saveMin}
                />
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── ВКЛАДКА: КОНТРАГЕНТЫ ─────────────────────────────────────────────────────
function ClientsTab() {
  const [clients, setClients]   = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [clientSearch, setClientSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ name: '', inn_bin: '', contact_person: '', phone: '', type: 'buyer',
      default_dsp_maker: '', default_pack_type: '' });
  const [saving, setSaving]     = useState(false);
  const [editId, setEditId]     = useState(null);
  const [editForm, setEditForm] = useState({ name: '', inn_bin: '', contact_person: '', phone: '', type: 'buyer',
      default_dsp_maker: '', default_pack_type: '' });
  const [editSaving, setEditSaving] = useState(false);

  const load = () => salesAPI.getClients().then(r => setClients(r.data));
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.name) { toast.error(t("Введите название")); return; }
    setSaving(true);
    try {
      await salesAPI.createClient(form);
      toast.success(t("Клиент добавлен"));
      setForm({ name: '', inn_bin: '', contact_person: '', phone: '', type: 'buyer',
      default_dsp_maker: '', default_pack_type: '' });
      setShowForm(false);
      load();
    } catch { toast.error(t("Ошибка")); }
    finally { setSaving(false); }
  };

  const startEdit = (c) => {
    setEditId(c.id);
    setEditForm({ name: c.name, inn_bin: c.inn_bin || '', contact_person: c.contact_person || '',
                  phone: c.phone || '', type: c.type,
                  // Пусто — подставляем справочный: чаще всего писать надо
                  // именно на него, и менеджеру остаётся нажать «Сохранить».
                  // После этого номер наш: обмен его не тронет.
                  notify_phone: c.notify_phone || c.phone || '',
                  default_dsp_maker: c.default_dsp_maker || '',
                  default_pack_type: c.default_pack_type || '' });
  };

  const handleUpdate = async () => {
    if (!editForm.name) { toast.error(t("Введите название")); return; }
    setEditSaving(true);
    try {
      // У выгруженного в 1С отправляем ТОЛЬКО свои поля. Не потому, что
      // сервер иначе не пропустит (он как раз не пропустит), а чтобы не
      // отправлять неизменённое: любое лишнее поле в запросе выглядит как
      // попытка переписать 1С.
      const locked = (clients.find(c => c.id === editId) || {}).is_synced_1c;
      await salesAPI.updateClient(editId, locked
        ? { default_dsp_maker: editForm.default_dsp_maker,
            default_pack_type: editForm.default_pack_type,
            notify_phone: editForm.notify_phone }
        : editForm);
      toast.success(t("Сохранено"));
      setEditId(null);
      load();
    } catch { toast.error(t("Ошибка")); }
    finally { setEditSaving(false); }
  };

  // Контрагентов несколько тысяч, и список отсортирован по алфавиту: чтобы
  // дойти до нужного, менеджер крутил колесо. Ищем по имени, по контактному
  // лицу, по ИИН/БИН и по телефону — по тому, что человек помнит, когда
  // клиент звонит: имя он может не расслышать, а номер видит на экране.
  //
  // Телефон сверяем ещё и цифрами подряд: в базе он записан как
  // «+998000000000», а набирают его как придётся — со скобками, пробелами
  // или просто хвост «0000». Без этого поиск по номеру не работал бы вовсе.
  const digits = (v) => String(v || '').replace(/\D/g, '');
  const match = makeMatcher(clientSearch);
  const digitsQ = digits(clientSearch);
  const filtered = clients.filter(c => {
    if (typeFilter === 'buyer' && !['buyer', 'both'].includes(c.type)) return false;
    if (typeFilter === 'supplier' && !['supplier', 'both'].includes(c.type)) return false;
    if (!clientSearch.trim()) return true;
    if (match(c.name, c.contact_person, c.inn_bin, c.phone, c.notify_phone)) return true;
    if (digitsQ.length >= 3) {
      const tel = digits(c.phone) + ' ' + digits(c.notify_phone) + ' ' + digits(c.inn_bin);
      if (tel.includes(digitsQ)) return true;
    }
    return false;
  });

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <STitle>{t('👥 Контрагенты')}</STitle>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={clientSearch} onChange={e => setClientSearch(e.target.value)}
            placeholder={t('🔍 Имя, телефон или ИИН/БИН…')}
            style={{
              width: 280, padding: '7px 12px', background: 'var(--bg)',
              border: '1px solid var(--border)', borderRadius: 8,
              color: 'var(--text)', fontSize: 13, outline: 'none',
              fontFamily: 'inherit',
            }} />
          {clientSearch && (
            <span style={{ color: 'var(--dim)', fontSize: 12, whiteSpace: 'nowrap' }}>
              {filtered.length}
            </span>
          )}
          {['all', 'buyer', 'supplier'].map(t => (
            <button key={t} onClick={() => setTypeFilter(t)} style={{
              padding: '5px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
              background: typeFilter === t ? 'var(--accent)' : 'var(--bg)',
              border: '1px solid ' + (typeFilter === t ? 'var(--accent)' : 'var(--border)'),
              color: typeFilter === t ? '#fff' : 'var(--muted)', fontWeight: 600,
            }}>
              {{ all: 'Все', buyer: 'Покупатели', supplier: 'Поставщики' }[t]}
            </button>
          ))}
          <Btn size="sm" onClick={() => setShowForm(!showForm)}>{t('+ Добавить')}</Btn>
        </div>
      </div>

      {showForm && (
        <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 16, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* ИИН/БИН — самое полезное поле для обмена: 1С ищет контрагента по
              РНН и при совпадении находит его сама. Пусто — документ ждёт,
              пока бухгалтер сопоставит запись руками. */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 10 }}>
            <Inp label="Название" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t("ООО «Компания»")} />
            <Inp label="ИИН / БИН" value={form.inn_bin}
              onChange={e => setForm({ ...form, inn_bin: e.target.value.replace(/\D/g, '').slice(0, 12) })}
              placeholder={t("12 цифр — 1С найдёт сама")} />
            <Inp label="Контактное лицо" value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} />
            <Inp label="Телефон" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
          {/* Что этот заказчик просит ВСЕГДА. Производителя плиты и обвязку
              он называет не раз в жизни, а каждый раз одинаково, и менеджер
              набирал это в каждой позиции каждого заказа заново — по памяти о
              разговоре, при котором его могло и не быть.

              Записанное здесь подставляется в новую позицию само. Именно
              подставляется, а не навязывается: в заказе поле обычное. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <SuggestInput size="md" kind={KIND_DSP}
              label="Обычно просит ДСП производителя" value={form.default_dsp_maker}
              onChange={v => setForm({ ...form, default_dsp_maker: v })}
              placeholder={t('необязательно')} />
            <SuggestInput size="md" kind={KIND_PACK}
              label="Обычная упаковка" value={form.default_pack_type}
              onChange={v => setForm({ ...form, default_pack_type: v })}
              placeholder={t('необязательно')} />
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <Sel label="Тип" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={{ width: 220 }}>
              <option value="buyer">{t('Покупатель')}</option>
              <option value="supplier">{t('Поставщик')}</option>
              <option value="both">{t('Покупатель + Поставщик')}</option>
            </Sel>
            <Btn onClick={handleCreate} disabled={saving}>{saving ? 'Сохранение...' : 'Сохранить'}</Btn>
            <Btn variant="ghost" onClick={() => setShowForm(false)}>{t('Отмена')}</Btn>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 'calc(100vh - 210px)', overflowY: 'auto' }}>
        {filtered.map(c => {
          const cfg = CLIENT_TYPE_CFG[c.type] || {};
          const isEditing = editId === c.id;
          return (
            <div key={c.id} style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px' }}>
              {isEditing && c.is_synced_1c ? (
                /* Выгруженного в 1С правим только в своей части. Название,
                   ИИН и тип принадлежат 1С: правка здесь разъедется с
                   бухгалтерией и вернётся обратно следующим обменом. */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                    <b style={{ color: 'var(--text)' }}>{c.name}</b> — карточка из 1С.
                    Название, ИИН и тип приходят оттуда. Здесь задаются телефон
                    для уведомлений и пожелания по заказу — их 1С не трогает.
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
                                alignItems: 'end' }}>
                    <Inp label="📱 Телефон для уведомлений"
                      value={editForm.notify_phone}
                      onChange={e => setEditForm(p => ({ ...p, notify_phone: e.target.value }))}
                      placeholder={t('+7 700 000 00 00')} />
                    {/* Справочный номер рядом и только для чтения: видно, что
                        мы пишем не туда, куда звонит бухгалтерия. */}
                    <div style={{ fontSize: 12, color: 'var(--dim)', paddingBottom: 10 }}>
                      {c.phone
                        ? <>Телефон из 1С: <b style={{ color: 'var(--muted)' }}>{c.phone}</b><br /></>
                        : <>В 1С телефона нет.<br /></>}
                      Сюда пойдут Telegram и WhatsApp о заказе.
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <SuggestInput size="md" kind={KIND_DSP}
                      label="Обычно просит ДСП производителя" value={editForm.default_dsp_maker}
                      onChange={v => setEditForm(p => ({ ...p, default_dsp_maker: v }))}
                      placeholder={t('необязательно')} />
                    <SuggestInput size="md" kind={KIND_PACK}
                      label="Обычная упаковка" value={editForm.default_pack_type}
                      onChange={v => setEditForm(p => ({ ...p, default_pack_type: v }))}
                      placeholder={t('необязательно')} />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn size="sm" variant="success" disabled={editSaving} onClick={handleUpdate}>
                      {editSaving ? '...' : '✓ Сохранить'}
                    </Btn>
                    <Btn size="sm" variant="ghost" onClick={() => setEditId(null)}>{t('Отмена')}</Btn>
                  </div>
                </div>
              ) : isEditing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 8 }}>
                    <Inp label="Название" value={editForm.name}
                      onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} />
                    <Inp label="ИИН / БИН" value={editForm.inn_bin}
                      onChange={e => setEditForm(p => ({ ...p, inn_bin: e.target.value.replace(/\D/g, '').slice(0, 12) }))} />
                    <Inp label="Контактное лицо" value={editForm.contact_person}
                      onChange={e => setEditForm(p => ({ ...p, contact_person: e.target.value }))} />
                    <Inp label="Телефон" value={editForm.phone}
                      onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} />
                  </div>
                  {/* Второй номер не прихоть: справочный принадлежит карточке
                      и уходит в 1С, а этот — тот, по которому человек читает
                      сообщения. Пусто — пишем на справочный. */}
                  <Inp label="📱 Телефон для уведомлений (Telegram / WhatsApp)"
                    value={editForm.notify_phone}
                    onChange={e => setEditForm(p => ({ ...p, notify_phone: e.target.value }))}
                    placeholder={t('если пусто — пишем на телефон из карточки')} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <SuggestInput size="md" kind={KIND_DSP}
                      label="Обычно просит ДСП производителя" value={editForm.default_dsp_maker}
                      onChange={v => setEditForm(p => ({ ...p, default_dsp_maker: v }))}
                      placeholder={t('необязательно')} />
                    <SuggestInput size="md" kind={KIND_PACK}
                      label="Обычная упаковка" value={editForm.default_pack_type}
                      onChange={v => setEditForm(p => ({ ...p, default_pack_type: v }))}
                      placeholder={t('необязательно')} />
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <Sel label="Тип" value={editForm.type}
                      onChange={e => setEditForm(p => ({ ...p, type: e.target.value }))}
                      style={{ width: 200 }}>
                      <option value="buyer">{t('Покупатель')}</option>
                      <option value="supplier">{t('Поставщик')}</option>
                      <option value="both">{t('Покупатель + Поставщик')}</option>
                    </Sel>
                    <Btn size="sm" variant="success" disabled={editSaving} onClick={handleUpdate}>
                      {editSaving ? '...' : '✓ Сохранить'}
                    </Btn>
                    <Btn size="sm" variant="ghost" onClick={() => setEditId(null)}>{t('Отмена')}</Btn>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600 }}>{c.name}</span>
                    {c.contact_person && <span style={{ color: 'var(--muted)', fontSize: 12, marginLeft: 10 }}>{c.contact_person}</span>}
                    {c.phone && <span style={{ color: 'var(--dim)', fontSize: 12, marginLeft: 10 }}>{c.phone}</span>}
                    {/* Свой номер — отдельным значком: по нему сразу видно,
                        кому из клиентов мы вообще можем написать. */}
                    {c.notify_phone && c.notify_phone !== c.phone && (
                      <span title={t('Телефон для уведомлений — задан у нас, 1С его не меняет')}
                        style={{ color: '#22c55e', fontSize: 12, marginLeft: 10 }}>
                        📱 {c.notify_phone}
                      </span>
                    )}
                    {/* Постоянные пожелания видно сразу: менеджер по ним и
                        узнаёт, что заказ этому клиенту соберётся сам */}
                    {(c.default_dsp_maker || c.default_pack_type) && (
                      <span style={{ color: '#8b5cf6', fontSize: 12, marginLeft: 10 }}>
                        {[c.default_dsp_maker, c.default_pack_type].filter(Boolean).join(' · ')}
                      </span>
                    )}
                    {/* Без ИИН/БИН 1С не найдёт контрагента сама, и отгрузка
                        будет ждать, пока бухгалтер сопоставит его руками */}
                    {c.inn_bin ? (
                      <span style={{ color: 'var(--dim)', fontSize: 12, marginLeft: 10 }}>
                        ИИН/БИН: <b style={{ color: 'var(--muted)' }}>{c.inn_bin}</b>
                      </span>
                    ) : (
                      <span title={t('1С не найдёт контрагента сама — бухгалтеру придётся сопоставлять руками')}
                        style={{
                          background: '#f59e0b18', color: '#f59e0b', borderRadius: 5,
                          padding: '1px 7px', fontSize: 10, fontWeight: 700, marginLeft: 10,
                        }}>{t('нет ИИН/БИН')}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ background: `${cfg.color}18`, color: cfg.color, borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>
                      {cfg.label}
                    </span>
                    {c.is_synced_1c && (
                      <span title={t("Название, ИИН и тип приходят из 1С и здесь не меняются")}
                        style={{ background: '#3b82f618', color: '#3b82f6', borderRadius: 6, padding: '3px 9px', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        🔒 1С
                      </span>
                    )}
                    {/* Карандаш есть и у выгруженного: постоянные пожелания
                        заказчика — наши, 1С про них не знает, и запрещать их
                        из-за замка на чужих полях незачем. */}
                    <button
                      onClick={() => startEdit(c)}
                      title={c.is_synced_1c
                        ? t("Задать пожелания по заказу")
                        : t("Редактировать")}
                      style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--muted)', width: 28, height: 28, cursor: 'pointer', fontSize: 13 }}>
                      ✏️
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── ОТЧЕТ ПО ЗАКАЗАМ (Order Feasibility) ────────────────────────────────────
function OrderFeasibilityReport() {
  const [data, setData] = useState({ sufficient: [], insufficient: [] });
  const [loading, setLoading] = useState(true);
  // Отчёт идёт по тем же заказам, что и список: по умолчанию весь цех, а
  // «только мои» — здесь же, чтобы ответ на «хватает ли сырья» можно было
  // сузить до своей работы.
  const [mine, setMine] = useState(false);

  useEffect(() => {
    setLoading(true);
    salesAPI.getFeasibilityReport(mine ? { mine: 1 } : {})
      .then(r => setData(r.data))
      .catch(err => toast.error(t("Ошибка загрузки отчета")))
      .finally(() => setLoading(false));
  }, [mine]);

  const renderList = (list, isSufficient) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {list.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--dim)', background: 'var(--card)', borderRadius: 10 }}>
          Нет заказов
        </div>
      ) : list.map(o => (
        <div key={o.id} style={{
          background: 'var(--card)', borderRadius: 10, padding: '16px 20px',
          borderLeft: `4px solid ${isSufficient ? '#22c55e' : '#ef4444'}`
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Заказ #{o.order_number}</span>
            <span style={{ fontSize: 13, color: 'var(--dim)' }}>{new Date(o.created_at).toLocaleDateString('ru-RU')}</span>
          </div>
          <div style={{ fontSize: 14, color: 'var(--text)' }}>
            Клиент: <b>{o.client_name}</b>
          </div>
          {!isSufficient && o.shortages && (
            <div style={{ marginTop: 12, padding: 12, background: '#ef444410', borderRadius: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#ef4444', marginBottom: 6 }}>{t('Нехватка сырья:')}</div>
              {o.shortages.map((sh, idx) => (
                <div key={idx} style={{ fontSize: 13, color: 'var(--text)', marginBottom: 4 }}>
                  • {sh.material_name} (для {sh.product_name}) — нужно: <b>{sh.needed} {sh.unit}</b>, 
                  в наличии: <b>{sh.available} {sh.unit}</b>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--mid)' }}>{t('Загрузка отчета...')}</div>;

  return (
    <div>
    <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
      {[[false, 'Все по филиалу'], [true, 'Только мои']].map(([v, label]) => (
        <button key={String(v)} onClick={() => setMine(v)} style={{
          padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
          cursor: 'pointer', fontFamily: 'inherit',
          background: mine === v ? 'var(--accent)' : 'var(--bg)',
          border: '1px solid ' + (mine === v ? 'var(--accent)' : 'var(--border)'),
          color: mine === v ? '#fff' : 'var(--muted)',
        }}>{t(label)}</button>
      ))}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
      <div>
        <h3 style={{ color: '#22c55e', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          ✅ Достаточно сырья ({data.sufficient.length})
        </h3>
        {renderList(data.sufficient, true)}
      </div>
      <div>
        <h3 style={{ color: '#ef4444', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          ⚠️ Нехватка сырья ({data.insufficient.length})
        </h3>
        {renderList(data.insufficient, false)}
      </div>
    </div>
    </div>
  );
}

// ─── ГЛАВНАЯ СТРАНИЦА ──────────────────────────────────────────────────────────
//
// Раньше вход открывался прямо списком заказов. У менеджера, который только
// продаёт, это ещё как-то читалось; у плановика — нет: он заходит согласовать
// сегодняшнюю работу и выписать сменный план, а попадал в чужую таблицу и
// заново соображал, куда идти. То, зачем он пришёл, лежало двумя пунктами ниже
// в списке одинаковых строк.
//
// Теперь у него то же, что у технолога и кладовщика: дела крупными плитками, с
// числом ждущего. Плитки строятся по правам — у кого нет отгрузки, у того нет
// и плитки; у кого нет планирования, тот не видит согласования. Справочники
// стоят отдельной группой ниже: их открывают редко, и одинаковый вид с работой
// им только мешает.
export default function ManagerPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('home');
  const canPlan = sessionStorage.getItem('can_plan') === '1'
    || ['director', 'admin'].includes(sessionStorage.getItem('role'));
  const canShip = sessionStorage.getItem('can_ship') === '1'
    || ['director', 'admin'].includes(sessionStorage.getItem('role'));

  // Прессы нужны только согласованию — грузим их, когда до него дошли
  const [presses, setPresses] = useState([]);
  useEffect(() => {
    if (activeTab !== 'approval' || presses.length) return;
    productionAPI.getPresses().then(r => setPresses(r.data)).catch(() => {});
  }, [activeTab, presses.length]);

  // Числа на плитках: видно, есть ли работа, не открывая раздел. Считаем на
  // главном и при возврате на него — не по таймеру: заказ не появляется сам,
  // его заводит человек, и лишний запрос каждые полминуты тут ничего не ловит.
  const [overdue, setOverdue] = useState(0);
  const [pendingCnt, setPendingCnt] = useState(0);
  useEffect(() => {
    if (activeTab !== 'home') return;
    // На плитке — СВОИ просроченные, даже когда список показывает весь цех:
    // «44 просрочено» из чужих заказов менеджер читает на свой счёт, а решает
    // по ним не он.
    salesAPI.getOrders({ mine: 1 })
      .then(r => {
        const rows = r.data.results || r.data || [];
        setOverdue(rows.filter(o => {
          const info = getDeadlineInfo(o.deadline, o.status);
          return info && info.label.startsWith('Просрочен');
        }).length);
      })
      .catch(() => {});
    if (canPlan) {
      productionAPI.getApprovalTable()
        .then(r => setPendingCnt((r.data.rows || []).length))
        .catch(() => {});
    }
  }, [activeTab, canPlan]);

  // Дела дня. Порядок тот, в каком их делают: сперва решить, что запускать,
  // потом вести заказы, потом отдать товар.
  const HOME_TILES = [
    ...(canPlan ? [{ key: 'approval', icon: '✅', title: 'Что запустить в работу',
      hint: 'Согласовать заказы и раздать их прессам',
      count: pendingCnt, color: 'var(--accent)' }] : []),
    ...(canPlan ? [{ key: 'requests', icon: '📄', title: 'Заявки на производство',
      hint: 'Выписать сменный план и напечатать его для цеха',
      color: '#8b5cf6' }] : []),
    { key: 'orders', icon: '📋', title: 'Заказы',
      hint: 'Принять новый заказ и вести уже принятые',
      count: overdue, color: overdue > 0 ? '#ef4444' : '#0ea5e9' },
    // Щит прессов. Планируют, глядя на него: что стоит на каждом прессе,
    // сколько осталось и когда пресс освободится. Раньше менеджеру он не
    // открывался вовсе — планировщик шёл спрашивать к технологу.
    //
    // Ведёт на отдельный адрес, а не в раздел этой страницы: щит занимает
    // экран целиком, и рисовать его внутри карточки заказов незачем.
    { key: '/board', icon: '🏭', title: 'Работа на прессах',
      hint: 'Что распределено по прессам и сколько осталось — отсюда планируют следующее',
      color: '#f59e0b' },
    ...(canShip ? [{ key: 'shipnotes', icon: '🚚', title: 'Выписать отгрузку',
      hint: 'Распоряжение складу — что и сколько выдать клиенту',
      color: '#22c55e' }] : []),
  ];

  // Открывают реже, чем работают: сюда заходят свериться.
  const REF_TILES = [
    { key: 'feasibility', icon: '📊', title: 'Отчёт по заказам', small: true, color: '#0ea5e9',
      hint: 'Хватает ли сырья на то, что уже принято' },
    { key: 'sheet', icon: '🖨', title: 'Бланк заказов', small: true, color: '#8b5cf6',
      hint: 'Лист на бумагу: декоры и матрицы, в клетке — сколько уже выпущено' },
    { key: 'stock', icon: '📦', title: 'Склад сырья', small: true, color: '#64748b',
      hint: 'Сколько плёнки и плиты лежит прямо сейчас' },
    { key: 'clients', icon: '👥', title: 'Контрагенты', small: true, color: '#64748b',
      hint: 'Клиенты и поставщики: телефон, ИИН, постоянные пожелания' },
  ];

  return (
    <Layout title={t("Менеджер")} sections={buildGroups({ canPlan, canShip })}
            section={activeTab} onSection={setActiveTab}>

      {/* Уйти отсюда можно всегда одинаково — иначе выход ищут заново */}
      {activeTab !== 'home' && <BackHome onBack={() => setActiveTab('home')} />}

      {activeTab === 'home' && (<>
        <TilesHead title="Что делаем сегодня"
          hint="Числа рядом — сколько ждёт. Пусто значит сделано." />
        {/* Ключ, начинающийся со слэша, — это адрес, а не раздел: щит прессов
            живёт отдельной страницей и занимает экран целиком. */}
        <TileGrid tiles={HOME_TILES}
          onPick={(k) => (String(k).startsWith('/') ? navigate(k) : setActiveTab(k))} />
        <div style={{ marginTop: 22 }}>
          <TilesHead title="Справки"
            hint="Открывают, когда нужно свериться, а не когда работают" />
          <TileGrid tiles={REF_TILES} onPick={setActiveTab} min={230} />
        </div>
      </>)}

      {activeTab === 'orders'  && <OrdersTab />}
      {canShip && activeTab === 'shipnotes' && <ShipmentNotes />}
      {activeTab === 'stock'   && <StockTab />}
      {activeTab === 'clients' && <ClientsTab />}
      {activeTab === 'feasibility' && <OrderFeasibilityReport />}
      {activeTab === 'sheet' && <OrderSheet />}
      {canPlan && activeTab === 'approval' && (
        <ApprovalTable presses={presses} onApproved={() => {}} />
      )}
      {canPlan && activeTab === 'requests' && (
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)',
                      borderRadius: 12, padding: 18 }}>
          <ProductionRequests />
        </div>
      )}
    </Layout>
  );
}
